import { useEffect, useState } from "react";
import type React from "react";
import { useTranslation } from "react-i18next";
import { LanguagePreference } from "../components/LanguagePreference";
import { theme, card, btnPrimary, inputStyle } from "../theme";
import { RulerProgress } from "../components/Drawing";
import { LoadingBlock } from "../components/Spinner";
import { StandardLicencesPanel } from "../components/StandardLicencesPanel";
import { api, apiErrorMessage } from "../api/client";
import { useFrameworks, hasPaidStandard, UNPAID_FRAMEWORK_IDS, ESG_FRAMEWORK_IDS, CERTIFIABLE_FRAMEWORKS } from "../context/FrameworksContext";
import { frameworkLabel } from "../nav/frameworkMeta";
import { usePortalNav } from "../context/PortalNavContext";
import { useCurrentUser } from "../auth/useCurrentUser";
import { notifyAddonsChanged } from "../nav/addonsChanged";
import { safeHref, openSafe } from "../utils/safeHref";
import {
  gbp, gbpMo, TPRM_ADDON_PENCE, DORA_MONITORING_ADDON_PENCE, ESG_ADDON_PENCE, AUDITOR_ACCESS_ADDON_PENCE, JURISDICTION_ADDON_PENCE, JURISDICTION_ANNUAL_PENCE, HARDENING_BENCHMARK_PENCE,
  PAYG_PEOPLE_MAX, SCALING_CEILING_PENCE, SCALING_CONTRACT_NOTES, ENDPOINTS_INCLUDED, pciSaqFee, peopleScalingLabel, endpointScalingLabel, type PeopleScalingRule,
} from "../lib/pricing";

import { cbSafe } from "../lib/colourBlind";
interface Tier {
  id: string;
  level: number;
  name: string;
  description: string;
  basePlatformFeePence: number;
  includesConsultant?: boolean;  // consultant time billed on top (managed service or day/hour rate)
  includesAuditor?: boolean;     // certification bureau / auditor fees passed through on top
}

// What the client's consultancy charges for consultant time — set on the
// consultancy's Rate Card page and shown here so L4/L5 costs are visible up front.
interface ConsultancyRateEntry {
  label: string;
  dayPence: number;
  hourPence: number;
  monthPence: number;     // managed service monthly fee; 0 = not offered at this grade
  managedDays: number;
  managedHours: number;
}
interface ConsultancyOffer {
  hasConsultancy: boolean;
  consultancyName: string | null;
  rateCard: Record<string, ConsultancyRateEntry> | null;
  availableConsultants: number;
}
const RATE_GRADE_ORDER = ["senior", "consultant", "junior"];
function gbp0(pence: number) { return "£" + (pence / 100).toLocaleString("en-GB", { maximumFractionDigits: 0 }); }
function allotment(days: number, hours: number) {
  const parts: string[] = [];
  if (days > 0)  parts.push(`${days} day${days !== 1 ? "s" : ""}`);
  if (hours > 0) parts.push(`${hours} hr${hours !== 1 ? "s" : ""}`);
  return parts.join(" + ");
}

/** Explicit "what else you pay" panel for consultant-led tiers. */
function TierPassThroughNote({ tier, offer, compact = false }: { tier: Tier; offer: ConsultancyOffer | null; compact?: boolean }) {
  const consultant = tier.includesConsultant ?? tier.level >= 4;
  const auditor    = tier.includesAuditor    ?? tier.level >= 5;
  if (!consultant && !auditor) return null;
  const rc = offer?.hasConsultancy && offer.rateCard ? offer.rateCard : null;
  const managed = rc ? RATE_GRADE_ORDER.map((g) => rc[g]).filter((e) => e && e.monthPence > 0) : [];
  const tm      = rc ? RATE_GRADE_ORDER.map((g) => rc[g]).filter((e) => !!e) : [];
  return (
    <div style={{ marginTop: compact ? 8 : 12, padding: compact ? "8px 12px" : "10px 14px", borderRadius: 3, background: theme.color.warningBg, border: `1px solid ${theme.color.warning}`, fontSize: compact ? 11 : 12, color: theme.color.warningText, lineHeight: 1.55 }}>
      <div style={{ fontWeight: 700, marginBottom: 4 }}>
        Not included in the platform fee{consultant && auditor ? " — consultant time and auditor fees" : consultant ? " — consultant time" : " — auditor fees"}
      </div>
      {consultant && (
        <div>
          <strong>Consultant time</strong> is charged by {offer?.consultancyName ? <strong>{offer.consultancyName}</strong> : "your consultancy"} in addition, as either a monthly managed service fee or a day/hour rate:
          {rc ? (
            <ul style={{ margin: "4px 0 0", paddingLeft: 18 }}>
              {managed.length > 0 ? (
                <li>
                  Managed service: {managed.map((e, i) => (
                    <span key={e.label}>{i > 0 ? " · " : ""}{gbp0(e.monthPence)}/month ({e.label}, includes {allotment(e.managedDays, e.managedHours)})</span>
                  ))}
                </li>
              ) : (
                <li>Managed service: not currently offered — time &amp; materials only</li>
              )}
              <li>
                Day / hour rates: {tm.map((e, i) => (
                  <span key={e.label}>{i > 0 ? " · " : ""}{e.label} {gbp0(e.dayPence)}/day, {gbp0(e.hourPence)}/hr</span>
                ))}
              </li>
              {offer && offer.availableConsultants > 0 && (
                <li>{offer.availableConsultants} consultant{offer.availableConsultants !== 1 ? "s" : ""} available per working day</li>
              )}
            </ul>
          ) : (
            <span> rates are agreed with the consultancy that manages your account.</span>
          )}
        </div>
      )}
      {auditor && (
        <div style={{ marginTop: consultant ? 4 : 0 }}>
          <strong>Certification bureau / auditor fees</strong> are passed through at cost on top of the platform fee — see the Assessment Bureau section for each bureau's certification and annual fees.
        </div>
      )}
    </div>
  );
}

interface Bureau {
  id: string;
  name: string;
  accreditation: string | null;
  country: string | null;
}

// One certification bureau per standard, each with its own contract lock.
interface Engagement {
  frameworkId: string;
  bureau: Bureau;
  selectedAt: string;
  lockedUntil: string | null;
  locked: boolean;
}

// Standards a certification bureau can be accredited to audit — the only ones
// a bureau can be engaged for (the shared list behind PUT /auditor/frameworks).
// Regulatory frameworks (GDPR, NIST CSF...) are not certified and never appear
// in the engagement list.
const CERTIFIABLE_FRAMEWORK_SET: ReadonlySet<string> = new Set(CERTIFIABLE_FRAMEWORKS);

interface FrameworkPrice {
  frameworkId:    string;
  certFeePence:   number;
  annualFeePence: number;
}

interface BureauListItem {
  id: string;
  name: string;
  accreditation:  string | null;
  country:        string | null;
  description:    string | null;
  website:        string | null;
  contactEmail:   string | null;
  frameworks:     string[];
  frameworkPrices: FrameworkPrice[];
  enrolledPrices: FrameworkPrice[];
  engagedFrameworkIds: string[];
}

interface Organization {
  id: string;
  serviceTier: Tier;
  apiFrequency: string;
  isDemo?: boolean;
  currentMonthSpendPence: number;
  spendCapPence: number;
  engagements: Engagement[];
  consultancyDaysPerMonth: number;
  consultancyHoursRemaining: number;
  billingMode: string;
  managedServiceRatePence: number | null;
  managedServiceDaysPerMonth: number;
  managedServiceHoursPerMonth: number;
  geography: string | null;
}

// EU/EEA member states subject to DORA (Regulation (EU) 2022/2554)
const EU_DORA_COUNTRIES = new Set([
  "Austria", "Belgium", "Bulgaria", "Croatia", "Cyprus", "Czech Republic", "Denmark",
  "Estonia", "Finland", "France", "Germany", "Greece", "Hungary", "Iceland", "Ireland",
  "Italy", "Latvia", "Liechtenstein", "Lithuania", "Luxembourg", "Malta", "Netherlands",
  "Norway", "Poland", "Portugal", "Romania", "Slovakia", "Slovenia", "Spain", "Sweden",
]);

interface UsageEvent {
  id: string;
  eventType: string;
  tokensIn: number;
  tokensOut: number;
  costPence: number;
  createdAt: string;
}

interface UsageSummary {
  currentMonthSpendPence: number;
  spendCapPence: number;
  spendResetsAt: string | null;
  tokenBudget: number;
  tokenRefreshPeriod: string;
  tokensUsedThisPeriod: number;
  tokenPeriodResetsAt: string | null;
  totalTokensUsed: number;
  events: UsageEvent[];
  consultancyAiCheckCap: number | null;
  aiChecksThisPeriod: number;
  aiCheckPeriodResetsAt: string | null;
  hasConsultancyAi: boolean;
  jurisdictionAddonPence: number;
  jurisdictionCount: number;
  auditorAmountPence?: number;
  platformFeePence?: number;
  /** The standards line as billed (backend lib/pricing.ts platformFeeBreakdown): on a package, only what it does not include. */
  standardsFeePence?: number;
  billingModel?: "small_business" | "bundle" | "enterprise" | "payg" | "jersey" | "jersey_licensed";
  /** The package (CE Annual Package or a bundle) the organisation is on; its price includes the tier. */
  package?: { id: string; name: string; tierLevel: number; tierFixed: boolean; allowedFrameworkIds: string[] | null } | null;
  // Enterprise scaling — people in scope (the headcount in My Organisation) and the managed
  // endpoints discovery found, both read on the server (backend lib/pricing.ts).
  headcount?: number | null;
  peopleScalingPence?: number;
  peopleScalingRule?: PeopleScalingRule;
  enterpriseRequired?: boolean;
  endpointCount?: number;
  endpointScalingPence?: number;
  dedicatedQuote?: boolean;
}

interface Addons {
  tprm:              boolean;
  doraMonitoring:    boolean;
  legalObligations:  boolean;
  esg:               boolean;
  auditorAccess?:    boolean;
  hardening?:        boolean;
}

interface HardeningBenchmark {
  id:          string;
  name:        string;
  version:     string;
  category:    string;
  description: string;
  subscribed:  boolean;
}

const HARDENING_CATEGORIES: Array<{ id: string; label: string }> = [
  { id: "cloud",            label: "Microsoft 365 & Azure" },
  { id: "endpoint",         label: "Endpoint Protection" },
  { id: "intune",           label: "Microsoft Intune" },
  { id: "windows-server",   label: "Windows Server" },
  { id: "windows-desktop",  label: "Windows Desktop" },
];

interface CheckCostEstimate {
  costPerCheckPence:    number;
  source:               "historical" | "estimate";
  historicalChecksUsed: number;
  frameworkCount:       number;
  currentFrequency:     string;
  costByFrequency: Record<string, { checksPerYear: number; annualCostPence: number }>;
}

interface Invoice {
  id: string;
  periodStart: string;
  periodEnd: string;
  tierAmountPence: number;
  usageAmountPence: number;
  consultancyAmountPence: number;
  auditorAmountPence?: number;
  totalAmountPence: number;
  status: string;
  createdAt: string;
}

interface TierChangeRequest {
  id: string;
  status: string;
  requestedAt: string;
  requestedTier: Tier;
}

interface OrgFramework {
  id: string;
  frameworkId: string;
  enrolledAt: string;
  pciSaqTypes?: string[];
}

// Voluntary certification standards — priced per framework
const ALL_FRAMEWORKS: Array<{ id: string; name: string; subtitle: string }> = [
  { id: "iso27001",              name: "ISO 27001",           subtitle: "Information Security Management System" },
  { id: "cyber_essentials",      name: "Cyber Essentials",    subtitle: "UK Government cybersecurity baseline" },
  { id: "cyber_essentials_plus", name: "Cyber Essentials Plus",   subtitle: "Independently verified Cyber Essentials" },
  { id: "iso42001",              name: "ISO 42001",           subtitle: "Artificial Intelligence Management System" },
  { id: "iso9001",               name: "ISO 9001",            subtitle: "Quality Management System" },
  { id: "soc2",                  name: "SOC 2",               subtitle: "Security, Availability & Confidentiality (AICPA)" },
  { id: "iso45001",              name: "ISO 45001",           subtitle: "Occupational Health & Safety Management" },
  { id: "ncsc_caf",              name: "NCSC CAF v4.0",       subtitle: "Cyber Assessment Framework for operators of essential services" },
];

// Reference frameworks — guidance with no certification workflow, SOA or assessment.
// Free alongside a paid standard: switched on or off in place (saved immediately, no
// framework fee) and never an organisation's only enrolment — the server refuses that
// (FREE_REFERENCE_FRAMEWORKS in context/FrameworksContext.tsx mirrors the backend).
const REFERENCE_FRAMEWORKS: Array<{ id: string; name: string; subtitle: string }> = [
  { id: "nist_csf", name: "NIST CSF 2.0", subtitle: "NIST Cybersecurity Framework — reference guide and maturity self-assessment; no certification workflow" },
];
const REFERENCE_IDS = new Set(REFERENCE_FRAMEWORKS.map((f) => f.id));

// Framework IDs that carry no framework fee of their own: regulations (included in
// the £49/mo jurisdiction add-on), the free reference frameworks above, and the two
// ESG standards, which the Environment & Sustainability Governance add-on pays for
// and enrols — they are never picked from the list below.
const NO_FEE_IDS: ReadonlySet<string> = new Set<string>([...UNPAID_FRAMEWORK_IDS, ...ESG_FRAMEWORK_IDS]);

const IASME_TIERS: Array<{ id: string; label: string; headcount: string }> = [
  { id: "iasme_ca_sole_trader",  label: "Sole Trader",    headcount: "1–2 people" },
  { id: "iasme_ca_micro",        label: "Micro",          headcount: "3–9 people" },
  { id: "iasme_ca_small",        label: "Small",          headcount: "10–49 people" },
  { id: "iasme_ca_medium_large", label: "Medium / Large", headcount: "50+ people" },
];

const PCI_SAQ_TYPES: Array<{ id: string; label: string; subtitle: string }> = [
  { id: "saq-a",          label: "SAQ A",                subtitle: "Card-not-present, all data fully outsourced" },
  { id: "saq-a-ep",       label: "SAQ A-EP",             subtitle: "E-commerce with partial outsourcing" },
  { id: "saq-b",          label: "SAQ B",                subtitle: "Imprint machines or standalone dial-out terminals" },
  { id: "saq-b-ip",       label: "SAQ B-IP",             subtitle: "Standalone IP-connected PTS devices" },
  { id: "saq-c",          label: "SAQ C",                subtitle: "Payment apps connected to internet" },
  { id: "saq-c-vt",       label: "SAQ C-VT",             subtitle: "Virtual terminals, single transaction entry" },
  { id: "saq-p2pe",       label: "SAQ P2PE",             subtitle: "Hardware terminals in a P2PE solution" },
  { id: "saq-d-merchant", label: "SAQ D (Merchant)",     subtitle: "All other merchant environments" },
  { id: "saq-d-sp",       label: "SAQ D (Service Provider)", subtitle: "All service provider environments" },
];


const FREQUENCY_OPTIONS: Array<{ id: string; label: string; sublabel: string }> = [
  { id: "annual",      label: "Annual",       sublabel: "1 check / year" },
  { id: "biannual",    label: "Bi-Annual",    sublabel: "2 checks / year" },
  { id: "quarterly",   label: "Quarterly",    sublabel: "4 checks / year" },
  { id: "monthly",     label: "Monthly",      sublabel: "12 checks / year" },
  { id: "fortnightly", label: "Fortnightly",  sublabel: "26 checks / year" },
  { id: "weekly",      label: "Weekly",       sublabel: "52 checks / year" },
  { id: "daily",       label: "Daily",        sublabel: "365 checks / year" },
  { id: "realtime",    label: "Real-time",    sublabel: "On every change" },
];

const secondaryBtn: React.CSSProperties = {
  border: `1px solid ${theme.color.borderStrong}`,
  background: "transparent",
  color: theme.color.muted,
  borderRadius: theme.radius.sm,
  padding: "9px 18px",
  fontSize: 13,
  fontWeight: 600,
  cursor: "pointer",
};

function formatEventType(eventType: string): string {
  const map: Record<string, string> = {
    ai_compliance_check: "Compliance Check",
    ai_policy_gen: "Policy Generation",
    ai_exec_report: "Executive Report",
    ai_finding_rec: "Finding Recommendation",
    ai_schedule_rec: "Schedule Recommendations",
    ai_risk_suggestion: "Risk Suggestions",
    ai_risk_evaluation: "Risk Evaluation",
    ai_hardening_assessment: "Hardening Assessment",
    ai_tprm_questionnaire_draft: "Supplier Questionnaire Draft",
    ai_operating_parameter_extraction: "Policy Figures Read",
    ai_subprocessor_extraction: "Sub-processor List Read",
    ai_scope_document: "Scope Document Draft",
    ai_customer_questionnaire: "Customer Questionnaire Answers",
    ai_evidence_quality: "Evidence Quality Review",
    ai_root_cause_draft: "Root-cause Draft",
    ai_intake_assess: "Intake Assessment",
    ai_contract_reading: "Supplier Agreement Read",
  };
  return map[eventType] ?? eventType;
}

function formatMonthPeriod(start: string): string {
  return new Date(start).toLocaleDateString("en-GB", { month: "long", year: "numeric" });
}

// The sub-line under the jurisdiction add-on's monthly figure. A package may include
// jurisdictions (or, on the CE Annual Package, bill the first one yearly), so the
// figure is explained from what the server billed rather than assumed to be £49 × N.
function jurisdictionFeeNote(usage: Pick<UsageSummary, "jurisdictionAddonPence" | "jurisdictionCount" | "billingModel">): string {
  const count = usage.jurisdictionCount;
  const pence = usage.jurisdictionAddonPence ?? 0;
  if (pence === count * JURISDICTION_ADDON_PENCE) return `${gbp(JURISDICTION_ADDON_PENCE)} × ${count}`;
  if (usage.billingModel === "small_business") {
    return `First at ${gbp(JURISDICTION_ANNUAL_PENCE)}/yr${count > 1 ? ` + ${gbp(JURISDICTION_ADDON_PENCE)} × ${count - 1}` : ""}`;
  }
  const billed = Math.min(count, Math.round(pence / JURISDICTION_ADDON_PENCE));
  const included = count - billed;
  if (billed === 0) return `${count} included in your package`;
  return `${included} included · ${gbp(JURISDICTION_ADDON_PENCE)} × ${billed}`;
}

const statusBadgeStyle = (status: string): React.CSSProperties => {
  const colours: Record<string, { bg: string; color: string }> = {
    draft:   { bg: theme.color.border,      color: theme.color.muted },
    sent:    { bg: theme.color.accentBg,    color: theme.color.accentText },
    paid:    { bg: theme.color.successBg,     color: theme.color.successText },
    overdue: { bg: theme.color.dangerBg,      color: theme.color.dangerText },
  };
  const c = colours[status] ?? colours.draft;
  return { fontSize: 11, fontWeight: 700, padding: "3px 8px", borderRadius: 3, background: c.bg, color: c.color, textTransform: "capitalize" };
};

/**
 * `isConsultant` switches the copy and the invoice button to the consultant's
 * point of view. It defaults to "rendered inside the consultant read-through"
 * (PortalNavContext is provided there and nowhere else).
 */
export function MyServicesPage({ isConsultant }: { isConsultant?: boolean } = {}) {
  const inReadThrough = usePortalNav() !== null;
  const consultantView = isConsultant ?? inReadThrough;
  const { reload: reloadFrameworks } = useFrameworks();
  // A Finance Contact (billing:view) reads this page; changing the plan stays an administrator's.
  const { user } = useCurrentUser();
  const readOnly = !consultantView && user?.role === "client" && !user.permissions?.includes("org:admin");
  const [org, setOrg] = useState<Organization | null>(null);
  const [tiers, setTiers] = useState<Tier[]>([]);
  const [consultancyOffer, setConsultancyOffer] = useState<ConsultancyOffer | null>(null);
  const [tierRequest, setTierRequest] = useState<TierChangeRequest | null | undefined>(undefined);
  const [requestedTierId, setRequestedTierId] = useState<string | null>(null);

  // Bureau picker — one engagement per standard
  const [bureauList, setBureauList] = useState<BureauListItem[]>([]);
  const [bureauSaving, setBureauSaving] = useState(false);
  const [engagingFrameworkId, setEngagingFrameworkId] = useState<string | null>(null);
  const [pendingBureauId, setPendingBureauId] = useState<string | null>(null);
  const [bureauError, setBureauError] = useState("");

  // AI frequency
  const [frequencySaving, setFrequencySaving] = useState(false);

  // Token budget
  const [tokenBudgetInput, setTokenBudgetInput] = useState("");
  const [tokenPeriodInput, setTokenPeriodInput] = useState("monthly");
  const [tokenSaving, setTokenSaving] = useState(false);
  const [tokenSaved, setTokenSaved] = useState(false);

  // Spend cap (advanced/secondary)
  const [capInput, setCapInput] = useState("");
  const [capSaving, setCapSaving] = useState(false);
  const [capSaved, setCapSaved] = useState(false);

  // Compliance frameworks. `selectedFrameworks` is the pending selection of the
  // priced grid (plus the regulations the server derives); the free reference
  // frameworks live only in `enrolledIds`, the enrolment as last saved, because
  // they switch on and off in place rather than through "Save selection".
  const [selectedFrameworks, setSelectedFrameworks] = useState<string[]>([]);
  const [enrolledIds, setEnrolledIds] = useState<string[]>([]);
  const [frameworksSaving, setFrameworksSaving] = useState(false);
  const [frameworksSaved, setFrameworksSaved] = useState(false);
  const [frameworksError, setFrameworksError] = useState<string | null>(null);
  const [iasmeOpen, setIasmeOpen] = useState(false);
  const [pciOpen, setPciOpen] = useState(false);
  const [selectedPciSaqs, setSelectedPciSaqs] = useState<string[]>([]);
  const [savingReference, setSavingReference] = useState<string | null>(null);
  const [referenceError, setReferenceError] = useState<string | null>(null);

  // Check cost estimate
  const [costEstimate, setCostEstimate] = useState<CheckCostEstimate | null>(null);

  // Add-ons
  const [addons, setAddons] = useState<Addons | null>(null);
  const [savingTprm, setSavingTprm] = useState(false);
  const [tprmError, setTprmError] = useState<string | null>(null);
  const [savingLegal, setSavingLegal] = useState(false);
  const [legalError, setLegalError] = useState<string | null>(null);
  const [savingEsg, setSavingEsg] = useState(false);
  const [esgError, setEsgError] = useState<string | null>(null);
  const [savingAuditor, setSavingAuditor] = useState(false);
  const [auditorError, setAuditorError] = useState<string | null>(null);

  // CIS Hardening benchmarks
  const [hardeningBenchmarks, setHardeningBenchmarks] = useState<HardeningBenchmark[]>([]);
  const [hardeningLoading, setHardeningLoading] = useState(true);
  const [hardeningOpen, setHardeningOpen] = useState(false);
  const [hardeningToggles, setHardeningToggles] = useState<Record<string, boolean>>({});
  const [hardeningCatOpen, setHardeningCatOpen] = useState<Record<string, boolean>>({});
  const [hardeningError, setHardeningError] = useState<string | null>(null);

  // Billing arrangement (managed service)
  const [switchingBillingMode, setSwitchingBillingMode] = useState(false);
  const [billingModeError, setBillingModeError] = useState<string | null>(null);

  // Language preference
  const { t } = useTranslation();
  // Usage & billing
  const [usage, setUsage] = useState<UsageSummary | null>(null);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [showAllEvents, setShowAllEvents] = useState(false);
  const [generatingInvoice, setGeneratingInvoice] = useState(false);
  const [invoiceError, setInvoiceError] = useState<string | null>(null);

  useEffect(() => {
    Promise.all([
      api.get<{ organization: Organization }>("/organizations/profile").catch(() => null),
      api.get<{ tiers: Tier[] }>("/organizations/service-tiers").catch(() => ({ tiers: [] as Tier[] })),
      api.get<{ request: TierChangeRequest | null }>("/organizations/tier-change-requests").catch(() => ({ request: null })),
      api.get<UsageSummary>("/usage").catch(() => null),
      api.get<{ invoices: Invoice[] }>("/usage/invoices").catch(() => ({ invoices: [] })),
      api.get<{ frameworks: OrgFramework[] }>("/organizations/frameworks").catch(() => ({ frameworks: [] })),
      api.get<{ bureaus: BureauListItem[] }>("/organizations/bureaus").catch(() => ({ bureaus: [] })),
      api.get<{ addons: Addons }>("/organizations/addons").catch(() => ({ addons: { tprm: false, doraMonitoring: false, legalObligations: true, esg: false } })),
      api.get<CheckCostEstimate>("/organizations/check-cost-estimate").catch(() => null),
      api.get<{ benchmarks: HardeningBenchmark[] }>("/hardening/benchmarks").catch(() => ({ benchmarks: [] })),
      api.get<ConsultancyOffer>("/organizations/consultancy-offer").catch(() => null),
    ]).then(([o, t, r, u, inv, fw, bList, ad, cost, hrd, offer]) => {
      if (o) setOrg(o.organization);
      setTiers(t.tiers);
      if (offer) setConsultancyOffer(offer);
      setTierRequest(r.request);
      if (u) setUsage(u);
      setInvoices(inv.invoices);
      const enrolledNow = fw.frameworks.map((f) => f.frameworkId);
      setEnrolledIds(enrolledNow);
      setSelectedFrameworks(enrolledNow.filter((id) => !REFERENCE_IDS.has(id)));
      const pcifw = fw.frameworks.find((f) => f.frameworkId === "pci_dss");
      if (pcifw?.pciSaqTypes?.length) setSelectedPciSaqs(pcifw.pciSaqTypes ?? []);
      setBureauList(bList.bureaus);
      setAddons(ad.addons);
      if (cost) setCostEstimate(cost);
      setHardeningBenchmarks(hrd.benchmarks);
      const toggles: Record<string, boolean> = {};
      for (const b of hrd.benchmarks) toggles[b.id] = b.subscribed;
      setHardeningToggles(toggles);
      setHardeningLoading(false);
    });
  }, []);

  const [requesting, setRequesting] = useState(false);
  const [requestError, setRequestError] = useState("");
  async function requestUpgrade(tierId: string) {
    // One click, one request: the button stays off until the answer is in (the server refuses a second open one too).
    if (requesting) return;
    setRequesting(true); setRequestError("");
    try {
      await api.post("/organizations/tier-change-requests", { requestedTierId: tierId });
      setRequestedTierId(tierId);
    } catch (err) {
      setRequestError(apiErrorMessage(err, "Could not send the request"));
    } finally {
      // Re-read it either way, so the pending banner (and its Withdraw button) has the request's id.
      const r = await api.get<{ request: TierChangeRequest | null }>("/organizations/tier-change-requests").catch(() => null);
      if (r) setTierRequest(r.request);
      setRequesting(false);
    }
  }

  const [withdrawing, setWithdrawing] = useState(false);
  const [withdrawError, setWithdrawError] = useState("");
  async function withdrawRequest(id: string) {
    setWithdrawing(true); setWithdrawError("");
    try {
      await api.post(`/organizations/tier-change-requests/${id}/withdraw`);
      setTierRequest((prev) => (prev ? { ...prev, status: "withdrawn" } : prev));
      setRequestedTierId(null);
    } catch (err) {
      setWithdrawError(apiErrorMessage(err, "Could not withdraw the request"));
    } finally {
      setWithdrawing(false);
    }
  }

  async function setFrequency(freq: string) {
    if (!org || freq === org.apiFrequency) return;
    setFrequencySaving(true);
    try {
      const result = await api.patch<{ apiFrequency: string }>("/organizations/api-frequency", { apiFrequency: freq });
      setOrg((prev) => (prev ? { ...prev, apiFrequency: result.apiFrequency } : prev));
      // Refresh cost estimate so the annual projection updates immediately
      api.get<CheckCostEstimate>("/organizations/check-cost-estimate").then(setCostEstimate).catch(() => null);
    } finally {
      setFrequencySaving(false);
    }
  }

  async function switchBillingMode(mode: string) {
    setSwitchingBillingMode(true); setBillingModeError(null);
    try {
      await api.patch("/billing/billing-mode", { billingMode: mode });
      setOrg((prev) => prev ? { ...prev, billingMode: mode } : prev);
    } catch {
      setBillingModeError("Could not switch billing arrangement. Please try again.");
    } finally {
      setSwitchingBillingMode(false);
    }
  }

  // Engage a bureau for one standard. The API locks that standard with the
  // bureau for 3 years and refuses a standard still locked elsewhere.
  async function engageBureau(frameworkId: string, bureauId: string) {
    setBureauSaving(true);
    setBureauError("");
    try {
      const result = await api.patch<{ ok: boolean; engagements: Engagement[] }>("/organizations/bureau", { bureauId, frameworkIds: [frameworkId] });
      setOrg((prev) => (prev ? { ...prev, engagements: result.engagements } : prev));
      setBureauList((prev) => prev.map((b) => ({
        ...b,
        engagedFrameworkIds: result.engagements.filter((e) => e.bureau.id === b.id).map((e) => e.frameworkId),
      })));
      setEngagingFrameworkId(null);
      setPendingBureauId(null);
    } catch (e: unknown) {
      setBureauError(e instanceof Error ? e.message : "Could not engage this bureau. Please try again.");
    } finally {
      setBureauSaving(false);
    }
  }

  const [capError, setCapError] = useState("");
  async function saveCap() {
    if (!org || !capInput) return;
    setCapError("");
    const pounds = parseFloat(capInput);
    if (!Number.isFinite(pounds) || pounds < 0) { setCapError("Enter an amount of £0 or more."); return; }
    const pence = Math.round(pounds * 100);
    // Under 1p would round to 0, which removes the cap: say so instead of doing it quietly.
    if (pence === 0 && pounds > 0) { setCapError("The smallest cap is £0.01. Use Remove cap to have no cap."); return; }
    if (pence === 0) { setCapError("A cap of £0 is no cap. Use Remove cap to have no cap."); return; }
    setCapSaving(true);
    try {
      const result = await api.patch<{ spendCapPence: number }>("/organizations/spend-cap", { spendCapPence: pence });
      setOrg((prev) => (prev ? { ...prev, spendCapPence: result.spendCapPence } : prev));
      setCapInput("");
      setCapSaved(true);
      setTimeout(() => setCapSaved(false), 3000);
    } catch (err) {
      setCapError(apiErrorMessage(err, "Could not save the cap"));
    } finally {
      setCapSaving(false);
    }
  }

  async function removeCap() {
    if (!org) return;
    setCapSaving(true);
    try {
      const result = await api.patch<{ spendCapPence: number }>("/organizations/spend-cap", { spendCapPence: 0 });
      setOrg((prev) => (prev ? { ...prev, spendCapPence: result.spendCapPence } : prev));
      setCapSaved(true);
      setTimeout(() => setCapSaved(false), 3000);
    } finally {
      setCapSaving(false);
    }
  }

  async function generateInvoice() {
    setGeneratingInvoice(true);
    setInvoiceError(null);
    try {
      const result = await api.post<{ invoice: Invoice }>("/usage/invoices/generate");
      const inv = result.invoice;
      setInvoices((prev) => [inv, ...prev]);
      await downloadInvoicePdf(inv);
    } catch (e: unknown) {
      const msg = e && typeof e === "object" && "message" in e ? String((e as { message: unknown }).message) : "Failed to generate invoice";
      setInvoiceError(msg);
    } finally {
      setGeneratingInvoice(false);
    }
  }

  async function downloadInvoicePdf(inv: Invoice) {
    const { jsPDF } = await import("jspdf");
    const doc = new jsPDF({ unit: "mm", format: "a4" });
    const pageW = doc.internal.pageSize.getWidth();
    const margin = 20;
    let y = margin;

    // Header bar
    doc.setFillColor(30, 30, 46);
    doc.rect(0, 0, pageW, 28, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(15);
    doc.setFont("helvetica", "bold");
    doc.text("Abacas Security", margin, 12);
    doc.setFontSize(8);
    doc.setFont("helvetica", "normal");
    doc.text("INVOICE  |  CONFIDENTIAL", margin, 20);
    doc.text(
      new Date().toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" }),
      pageW - margin,
      20,
      { align: "right" }
    );
    y = 40;

    // Invoice title
    doc.setTextColor(30, 30, 46);
    doc.setFontSize(20);
    doc.setFont("helvetica", "bold");
    doc.text("Invoice", margin, y);
    y += 8;

    // Period
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(100, 100, 120);
    doc.text(`Period: ${formatMonthPeriod(inv.periodStart)}`, margin, y);
    doc.text(`Status: ${inv.status.toUpperCase()}`, pageW - margin, y, { align: "right" });
    y += 6;
    doc.text(`Invoice ID: ${inv.id}`, margin, y);
    y += 10;

    // Divider
    doc.setDrawColor(200, 200, 210);
    doc.line(margin, y, pageW - margin, y);
    y += 8;

    // Table header
    doc.setFillColor(245, 245, 250);
    doc.rect(margin, y - 4, pageW - margin * 2, 8, "F");
    doc.setTextColor(80, 80, 100);
    doc.setFontSize(9);
    doc.setFont("helvetica", "bold");
    doc.text("Description", margin + 2, y + 1);
    doc.text("Amount", pageW - margin - 2, y + 1, { align: "right" });
    y += 10;

    // Line items
    doc.setFont("helvetica", "normal");
    doc.setTextColor(30, 30, 46);
    doc.setFontSize(10);

    const tierAmt = (inv.tierAmountPence / 100).toFixed(2);
    const usageAmt = (inv.usageAmountPence / 100).toFixed(2);
    const consultancyAmt = ((inv.consultancyAmountPence ?? 0) / 100).toFixed(2);
    const auditorAmt = ((inv.auditorAmountPence ?? 0) / 100).toFixed(2);
    const totalAmt = (inv.totalAmountPence / 100).toFixed(2);

    doc.text("Platform tier fee", margin + 2, y);
    doc.text(`£${tierAmt}`, pageW - margin - 2, y, { align: "right" });
    y += 8;

    doc.text("AI usage fee", margin + 2, y);
    doc.text(`£${usageAmt}`, pageW - margin - 2, y, { align: "right" });
    y += 8;

    if ((inv.consultancyAmountPence ?? 0) > 0) {
      doc.text("Confirmed consultancy time", margin + 2, y);
      doc.text(`£${consultancyAmt}`, pageW - margin - 2, y, { align: "right" });
      y += 8;
    }

    if ((inv.auditorAmountPence ?? 0) > 0) {
      doc.text("Certification bureau fee", margin + 2, y);
      doc.text(`£${auditorAmt}`, pageW - margin - 2, y, { align: "right" });
      y += 8;
    }
    y -= 2;

    // Divider before total
    doc.setDrawColor(200, 200, 210);
    doc.line(margin, y, pageW - margin, y);
    y += 6;

    // Total
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.text("Total", margin + 2, y);
    doc.text(`£${totalAmt}`, pageW - margin - 2, y, { align: "right" });

    const period = formatMonthPeriod(inv.periodStart).replace(" ", "-");
    doc.save(`abacas-invoice-${period}.pdf`);
  }

  const [tokenError, setTokenError] = useState("");
  async function saveTokenBudget(budget: number) {
    setTokenError("");
    if (!Number.isInteger(budget) || budget < 0) { setTokenError("Enter the budget as a whole number of tokens, such as 500000."); return; }
    setTokenSaving(true);
    try {
      await api.patch("/usage/token-budget", { tokenBudget: budget, tokenRefreshPeriod: tokenPeriodInput });
      const u = await api.get<UsageSummary>("/usage").catch(() => null);
      if (u) setUsage(u);
      setTokenBudgetInput("");
      setTokenSaved(true);
      setTimeout(() => setTokenSaved(false), 3000);
    } catch (err) {
      setTokenError(apiErrorMessage(err, "Could not save the budget"));
    } finally {
      setTokenSaving(false);
    }
  }

  function toggleFramework(id: string) {
    setSelectedFrameworks((prev) => {
      if (prev.includes(id)) return prev.filter((f) => f !== id);
      // CE and CE+ are mutually exclusive — selecting one removes the other
      if (id === "cyber_essentials")      return [...prev.filter((f) => f !== "cyber_essentials_plus"), id];
      if (id === "cyber_essentials_plus") return [...prev.filter((f) => f !== "cyber_essentials"),      id];
      return [...prev, id];
    });
    setFrameworksSaved(false);
    setFrameworksError(null);
  }

  function selectIasmeTier(tierId: string) {
    const otherTierIds = IASME_TIERS.map((t) => t.id).filter((id) => id !== tierId);
    setSelectedFrameworks((prev) => {
      const withoutOtherTiers = prev.filter((id) => !otherTierIds.includes(id));
      return withoutOtherTiers.includes(tierId)
        ? withoutOtherTiers.filter((id) => id !== tierId)
        : [...withoutOtherTiers, tierId];
    });
    setFrameworksSaved(false);
    setFrameworksError(null);
  }

  function selectPciSaq(saqId: string) {
    setSelectedPciSaqs((prev) => {
      const next = prev.includes(saqId) ? prev.filter((id) => id !== saqId) : [...prev, saqId];
      setSelectedFrameworks((fws) => {
        const withoutPci = fws.filter((id) => id !== "pci_dss");
        return next.length > 0 ? [...withoutPci, "pci_dss"] : withoutPci;
      });
      return next;
    });
    setFrameworksSaved(false);
    setFrameworksError(null);
  }

  // Reference frameworks are free, so they save on the spot rather than through
  // "Save selection". The new set is built from the enrolment on the server so an
  // unsaved change in the priced grid is neither committed nor lost; the server
  // refuses a reference framework with no paid standard beside it.
  async function toggleReferenceFramework(id: string) {
    setSavingReference(id);
    setReferenceError(null);
    try {
      const current = await api.get<{ frameworks: OrgFramework[] }>("/organizations/frameworks");
      const saved = current.frameworks.map((f) => f.frameworkId);
      const enable = !saved.includes(id);
      const next = enable ? [...saved, id] : saved.filter((f) => f !== id);
      const result = await api.put<{ frameworks: OrgFramework[] }>("/organizations/frameworks", { frameworkIds: next });
      setEnrolledIds(result.frameworks.map((f) => f.frameworkId));
      reloadFrameworks();
    } catch (e: unknown) {
      const msg = e && typeof e === "object" && "message" in e ? String((e as { message: unknown }).message) : "Failed to update reference frameworks";
      setReferenceError(msg);
    } finally {
      setSavingReference(null);
    }
  }

  // The reference frameworks switched on, as long as the selection being saved
  // still has a paid standard to carry them (they are free only alongside one).
  const enrolledReferenceIds = enrolledIds.filter((id) => REFERENCE_IDS.has(id));
  const saveDropsReference   = enrolledReferenceIds.length > 0 && !hasPaidStandard(selectedFrameworks);

  async function saveFrameworks() {
    setFrameworksSaving(true);
    setFrameworksError(null);
    try {
      const frameworkIds = saveDropsReference ? selectedFrameworks : [...selectedFrameworks, ...enrolledReferenceIds];
      const result = await api.put<{ frameworks: OrgFramework[] }>("/organizations/frameworks", { frameworkIds });
      setEnrolledIds(result.frameworks.map((f) => f.frameworkId));
      if (selectedFrameworks.includes("pci_dss")) {
        await api.put("/organizations/frameworks/pci_dss/saq", { saqTypes: selectedPciSaqs });
      }
      setFrameworksSaved(true);
      reloadFrameworks();
    } catch (e: unknown) {
      const msg = e && typeof e === "object" && "message" in e ? String((e as { message: unknown }).message) : "Failed to save frameworks";
      setFrameworksError(msg);
    } finally {
      setFrameworksSaving(false);
    }
  }

  if (!org) {
    return <LoadingBlock />;
  }

  const pendingRequest = tierRequest && tierRequest.status === "pending" ? tierRequest : null;
  // A package includes its tier: the CE Annual Package pins it, a bundle sets
  // the floor — only tiers the package allows are offered (the API refuses the rest).
  const pkg = usage?.package ?? null;
  const otherTiers = tiers.filter((t) => t.id !== org.serviceTier.id && (!pkg || (!pkg.tierFixed && t.level >= pkg.tierLevel)));
  const packageAllowsFramework = (id: string) => !pkg?.allowedFrameworkIds || pkg.allowedFrameworkIds.includes(id);
  const hardeningAvailable = org.serviceTier.level >= 3;
  // Standards a bureau can be engaged for, in the order they are enrolled.
  const certifiableFrameworks = selectedFrameworks.filter((id) => CERTIFIABLE_FRAMEWORK_SET.has(id));
  const showAi          = org.serviceTier.level >= 3;
  const showConsultancy = org.serviceTier.level >= 4 || org.consultancyDaysPerMonth > 0 || org.consultancyHoursRemaining > 0;

  const toggleTprm = async () => {
    if (!addons) return;
    setSavingTprm(true);
    setTprmError(null);
    try {
      const result = await api.patch<{ addons: Addons }>("/organizations/addons", { tprmEnabled: !addons.tprm });
      setAddons(result.addons);
      notifyAddonsChanged();
    } catch {
      setTprmError("Failed to update TPRM add-on. Please try again.");
    } finally {
      setSavingTprm(false);
    }
  };

  const toggleLegal = async () => {
    if (!addons) return;
    setSavingLegal(true);
    setLegalError(null);
    try {
      const result = await api.patch<{ addons: Addons }>("/organizations/addons", { legalObligationsEnabled: !addons.legalObligations });
      setAddons(result.addons);
      notifyAddonsChanged();
    } catch {
      setLegalError("Failed to update Legal Obligations add-on. Please try again.");
    } finally {
      setSavingLegal(false);
    }
  };

  const toggleEsg = async () => {
    if (!addons) return;
    setSavingEsg(true);
    setEsgError(null);
    try {
      const result = await api.patch<{ addons: Addons }>("/organizations/addons", { esgEnabled: !addons.esg });
      setAddons(result.addons);
      // The add-on enrols (or drops) ISO 14001 and IWA 48 — reload so the sidebar's
      // Environment & Sustainability section follows at once.
      reloadFrameworks();
      notifyAddonsChanged();
    } catch {
      setEsgError("Failed to update Environment & Sustainability Governance add-on. Please try again.");
    } finally {
      setSavingEsg(false);
    }
  };

  const toggleAuditorAccess = async () => {
    if (!addons) return;
    setSavingAuditor(true);
    setAuditorError(null);
    try {
      const result = await api.patch<{ addons: Addons }>("/organizations/addons", { auditorAccessEnabled: !addons.auditorAccess });
      setAddons(result.addons);
      notifyAddonsChanged();
    } catch {
      setAuditorError("Failed to update the auditor access add-on. Please try again.");
    } finally {
      setSavingAuditor(false);
    }
  };

  const toggleHardeningBenchmark = async (benchmarkId: string, subscribe: boolean) => {
    setHardeningError(null);
    setHardeningToggles((prev) => ({ ...prev, [benchmarkId]: subscribe }));
    try {
      if (subscribe) {
        await api.post("/hardening/subscriptions", { benchmarkId });
      } else {
        await api.del(`/hardening/subscriptions/${benchmarkId}`);
      }
      notifyAddonsChanged();
    } catch {
      setHardeningError("Failed to update benchmark subscription. Please try again.");
      setHardeningToggles((prev) => ({ ...prev, [benchmarkId]: !subscribe }));
    }
  };

  return (
    <div className="page-pad">
      <h1 style={{ margin: "0 0 6px", fontSize: 20, fontWeight: 700, color: theme.color.heading, letterSpacing: "-0.3px" }}>
        My Services
      </h1>
      <p style={{ margin: "0 0 24px", fontSize: 13, color: theme.color.subtle }}>
        Your current plan, AI usage settings, and billing configuration.
      </p>
      {readOnly && (
        <div style={{ ...card, marginBottom: 20, fontSize: 12, color: theme.color.subtle }}>
          You can see the plan, usage and invoices. Changing standards, the service tier, add-ons or budgets needs an administrator.
        </div>
      )}

      {/* ── A. Compliance Frameworks ──────────────────────────────────── */}
      <div style={{ ...card, marginBottom: 20 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: theme.color.heading, marginBottom: 4 }}>
          Compliance Frameworks
        </div>
        <div style={{ fontSize: 12, color: theme.color.subtle, marginBottom: 16 }}>
          Select the standards your organisation is working towards.
        </div>

        {/* A member without org:admin sees the selection, never a control that looks live
            (the server refuses the change either way). */}
        <fieldset disabled={readOnly} style={{ border: 0, padding: 0, margin: 0, minWidth: 0, opacity: readOnly ? 0.6 : 1, pointerEvents: readOnly ? "none" : undefined }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
          {ALL_FRAMEWORKS.map((fw) => {
            const active  = selectedFrameworks.includes(fw.id);
            const packageBlocked = !active && !packageAllowsFramework(fw.id);
            const blocked = packageBlocked
                         || (fw.id === "cyber_essentials"      && selectedFrameworks.includes("cyber_essentials_plus"))
                         || (fw.id === "cyber_essentials_plus" && selectedFrameworks.includes("cyber_essentials"));
            return (
              <button
                key={fw.id}
                onClick={() => !blocked && toggleFramework(fw.id)}
                disabled={blocked}
                title={packageBlocked ? `Not covered by the ${pkg?.name} — ask your consultant to move you to a bundle or pay-as-you-go` : blocked ? "CE and CE+ are mutually exclusive — deselect the other first" : undefined}
                style={{
                  display: "flex", alignItems: "flex-start", gap: 10,
                  padding: "10px 12px", borderRadius: theme.radius.md,
                  cursor: blocked ? "not-allowed" : "pointer",
                  textAlign: "left",
                  border: `1px solid ${active ? theme.color.accent : blocked ? theme.color.border : theme.color.border}`,
                  background: active ? theme.color.accentBg : blocked ? theme.color.panelAlt : "transparent",
                  opacity: blocked ? 0.45 : 1,
                  transition: "border-color 0.1s, background 0.1s, opacity 0.1s",
                }}
              >
                <div style={{
                  width: 14, height: 14, borderRadius: 3, flexShrink: 0, marginTop: 2,
                  border: `2px solid ${active ? theme.color.accent : theme.color.borderStrong}`,
                  background: active ? theme.color.accent : "transparent",
                }} />
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: active ? theme.color.heading : theme.color.text }}>
                    {fw.name}
                  </div>
                  <div style={{ fontSize: 11, color: theme.color.subtle, marginTop: 1 }}>
                    {packageBlocked ? `not covered by the ${pkg?.name}` : blocked ? "not available with the other CE selection" : fw.subtitle}
                  </div>
                </div>
              </button>
            );
          })}

          {/* IASME Cyber Assurance — grouped accordion spanning full width */}
          {(() => {
            const activeTier = IASME_TIERS.find((t) => selectedFrameworks.includes(t.id));
            const isOpen = iasmeOpen || !!activeTier;
            return (
              <div style={{ gridColumn: "1 / -1", border: `1px solid ${activeTier ? theme.color.accent : theme.color.border}`, borderRadius: theme.radius.md, background: activeTier ? theme.color.accentBg : "transparent", overflow: "hidden", transition: "border-color 0.1s, background 0.1s" }}>
                {/* Header row */}
                <button
                  onClick={() => setIasmeOpen((o) => !o)}
                  style={{ width: "100%", display: "flex", alignItems: "flex-start", gap: 10, padding: "10px 12px", cursor: "pointer", background: "transparent", border: "none", textAlign: "left" }}
                >
                  <div style={{
                    width: 14, height: 14, borderRadius: 3, flexShrink: 0, marginTop: 2,
                    border: `2px solid ${activeTier ? theme.color.accent : theme.color.borderStrong}`,
                    background: activeTier ? theme.color.accent : "transparent",
                  }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: activeTier ? theme.color.heading : theme.color.text }}>
                      IASME Cyber Assurance {activeTier ? `— ${activeTier.label}` : ""}
                    </div>
                    <div style={{ fontSize: 11, color: theme.color.subtle, marginTop: 1 }}>
                      {activeTier ? `${activeTier.headcount} · Click to change tier` : "Click to select your organisation size tier"}
                    </div>
                  </div>
                  <span style={{ fontSize: 11, color: theme.color.faint, flexShrink: 0, marginTop: 2 }}>
                    {isOpen ? "▲" : "▼"}
                  </span>
                </button>

                {/* Tier options */}
                {isOpen && (
                  <div style={{ padding: "10px 12px 12px", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, borderTop: `1px solid ${theme.color.border}` }}>
                    {IASME_TIERS.map((tier) => {
                      const active = selectedFrameworks.includes(tier.id);
                      return (
                        <button
                          key={tier.id}
                          onClick={() => selectIasmeTier(tier.id)}
                          style={{
                            display: "flex", alignItems: "flex-start", gap: 8,
                            padding: "8px 10px", borderRadius: 3, cursor: "pointer", textAlign: "left",
                            border: `1px solid ${active ? theme.color.accent : theme.color.border}`,
                            background: active ? theme.color.accent + "22" : theme.color.panelAlt,
                            transition: "all 0.1s",
                          }}
                        >
                          <div style={{
                            width: 12, height: 12, borderRadius: "50%", flexShrink: 0, marginTop: 2,
                            border: `2px solid ${active ? theme.color.accent : theme.color.borderStrong}`,
                            background: active ? theme.color.accent : "transparent",
                          }} />
                          <div>
                            <div style={{ fontSize: 12, fontWeight: 600, color: active ? theme.color.heading : theme.color.text }}>
                              {tier.label}
                            </div>
                            <div style={{ fontSize: 11, color: theme.color.subtle }}>{tier.headcount}</div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })()}

          {/* PCI DSS v4.0 — accordion with multi-select SAQ type selection */}
          {(() => {
            const pciActive = selectedFrameworks.includes("pci_dss");
            const isOpen = pciOpen || pciActive;
            const headerSub = pciActive && selectedPciSaqs.length > 0
              ? `${selectedPciSaqs.length} type${selectedPciSaqs.length > 1 ? "s" : ""} selected · Click to modify`
              : "Payment Card Industry Data Security Standard · Click to select your SAQ type(s)";
            return (
              <div style={{ gridColumn: "1 / -1", border: `1px solid ${pciActive ? theme.color.accent : theme.color.border}`, borderRadius: theme.radius.md, background: pciActive ? theme.color.accentBg : "transparent", overflow: "hidden", transition: "border-color 0.1s, background 0.1s" }}>
                {/* Header row */}
                <button
                  onClick={() => setPciOpen((o) => !o)}
                  style={{ width: "100%", display: "flex", alignItems: "flex-start", gap: 10, padding: "10px 12px", cursor: "pointer", background: "transparent", border: "none", textAlign: "left" }}
                >
                  <div style={{
                    width: 14, height: 14, borderRadius: 3, flexShrink: 0, marginTop: 2,
                    border: `2px solid ${pciActive ? theme.color.accent : theme.color.borderStrong}`,
                    background: pciActive ? theme.color.accent : "transparent",
                  }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: pciActive ? theme.color.heading : theme.color.text }}>
                      PCI DSS v4.0
                    </div>
                    <div style={{ fontSize: 11, color: theme.color.subtle, marginTop: 1 }}>
                      {headerSub}
                    </div>
                  </div>
                  <span style={{ fontSize: 11, color: theme.color.faint, flexShrink: 0, marginTop: 2 }}>
                    {isOpen ? "▲" : "▼"}
                  </span>
                </button>

                {/* SAQ type options — checkbox multi-select */}
                {isOpen && (
                  <div style={{ padding: "10px 12px 12px", display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 6, borderTop: `1px solid ${theme.color.border}` }}>
                    {PCI_SAQ_TYPES.map((saq) => {
                      const active = selectedPciSaqs.includes(saq.id);
                      return (
                        <button
                          key={saq.id}
                          onClick={() => selectPciSaq(saq.id)}
                          style={{
                            display: "flex", alignItems: "flex-start", gap: 8,
                            padding: "8px 10px", borderRadius: 3, cursor: "pointer", textAlign: "left",
                            border: `1px solid ${active ? theme.color.accent : theme.color.border}`,
                            background: active ? theme.color.accent + "22" : theme.color.panelAlt,
                            transition: "all 0.1s",
                          }}
                        >
                          <div style={{
                            width: 12, height: 12, borderRadius: 2, flexShrink: 0, marginTop: 2,
                            border: `2px solid ${active ? theme.color.accent : theme.color.borderStrong}`,
                            background: active ? theme.color.accent : "transparent",
                          }} />
                          <div>
                            <div style={{ fontSize: 12, fontWeight: 600, color: active ? theme.color.heading : theme.color.text }}>
                              {saq.label}
                            </div>
                            <div style={{ fontSize: 11, color: theme.color.subtle }}>{saq.subtitle}</div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })()}
        </div>

        {/* Reference frameworks — free, switched on or off in place */}
        <div style={{ marginTop: 16, fontSize: 11, fontWeight: 700, color: theme.color.faint, textTransform: "uppercase", letterSpacing: "0.05em" }}>
          Reference frameworks — free
        </div>
        {referenceError && <div style={{ fontSize: 12, color: theme.color.dangerText, marginTop: 6 }}>{referenceError}</div>}
        {REFERENCE_FRAMEWORKS.map((fw) => {
          const active     = enrolledIds.includes(fw.id);
          const saving     = savingReference === fw.id;
          // Free only alongside a paid standard, and the toggle saves on the spot —
          // so it needs one already saved, not merely ticked above.
          const canSwitchOn = hasPaidStandard(enrolledIds);
          const disabled   = savingReference !== null || (!active && !canSwitchOn);
          return (
            <div key={fw.id} style={{
              display: "flex", alignItems: "center", gap: 16,
              padding: "12px 14px", borderRadius: theme.radius.md, marginTop: 8,
              border: `1px solid ${active ? theme.color.accent : theme.color.border}`,
              background: active ? theme.color.accentBg : "transparent",
              transition: "border-color 0.15s, background 0.15s",
            }}>
              <div style={{
                width: 10, height: 10, borderRadius: "50%", flexShrink: 0,
                background: active ? theme.color.success : theme.color.border,
                boxShadow: active ? `0 0 0 3px ${theme.color.success}33` : "none",
              }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: active ? theme.color.heading : theme.color.text }}>{fw.name}</div>
                <div style={{ fontSize: 11, color: theme.color.subtle, marginTop: 1, lineHeight: 1.4 }}>{fw.subtitle}</div>
                <div style={{ fontSize: 11, color: !active && !canSwitchOn ? theme.color.warningText : theme.color.faint, marginTop: 3 }}>
                  {!active && !canSwitchOn
                    ? "Free alongside any standard — select a standard above and save your selection first."
                    : "Free alongside any standard; switching off your last standard switches it off too."}
                </div>
              </div>
              <div style={{ textAlign: "right", flexShrink: 0 }}>
                <div style={{ fontSize: 15, fontWeight: 700, color: active ? theme.color.accentText : theme.color.faint }}>Free</div>
                <div style={{
                  marginTop: 4, fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 3, display: "inline-block",
                  background: active ? theme.color.successBg : theme.color.border,
                  color: active ? theme.color.successText : theme.color.muted,
                  textTransform: "uppercase", letterSpacing: "0.04em",
                }}>
                  {active ? "On" : "Off"}
                </div>
                <button
                  onClick={() => toggleReferenceFramework(fw.id)}
                  disabled={disabled}
                  title={!active && !canSwitchOn ? "Enrol in a standard first" : undefined}
                  style={{
                    display: "block", marginTop: 6, marginLeft: "auto",
                    background: active ? "none" : theme.color.accent,
                    border: active ? `1px solid ${theme.color.border}` : "none",
                    borderRadius: 3, padding: "5px 12px", fontSize: 12, cursor: disabled ? "not-allowed" : "pointer",
                    color: active ? theme.color.subtle : "#fff",
                    opacity: disabled ? 0.5 : 1, fontWeight: 600,
                  }}
                >
                  {saving ? "Saving…" : active ? "Switch off" : "Switch on"}
                </button>
              </div>
            </div>
          );
        })}
        </fieldset>

        {/* Live monthly cost summary */}
        {(() => {
          const hasCe  = selectedFrameworks.includes("cyber_essentials");
          const hasCep = selectedFrameworks.includes("cyber_essentials_plus");
          const ceFee  = (hasCe ? 49 : 0) + (hasCep ? 149 : 0);
          const otherFrameworks = selectedFrameworks.filter((id) => !NO_FEE_IDS.has(id) && id !== "cyber_essentials" && id !== "cyber_essentials_plus");
          const nOther = otherFrameworks.length;
          const otherFee = nOther === 0 ? 0 : nOther === 1 ? 249 : 398 + (nOther - 2) * 99;
          const n = selectedFrameworks.filter((id) => !NO_FEE_IDS.has(id)).length;
          const pciSaqCount = selectedFrameworks.includes("pci_dss") ? selectedPciSaqs.length : 0;
          const pciSaqExtra = pciSaqFee(pciSaqCount) / 100;
          const monthlyFee = ceFee + otherFee + pciSaqExtra;
          const saqSuffix = pciSaqExtra > 0 ? ` + £${pciSaqExtra} PCI DSS SAQ types` : "";
          const ceSuffix = hasCe && hasCep ? " + CE £49 + CE+ £149" : hasCe ? " + CE £49" : hasCep ? " + CE+ £149" : "";
          // On a package the standards it includes are inside its flat price: quote what is billed for
          // the saved selection (the server's breakdown), never the pay-as-you-go ladder.
          if (pkg && n > 0) {
            const billed = usage?.standardsFeePence;
            return (
              <div style={{
                marginTop: 14, padding: "12px 16px", borderRadius: 3, background: theme.color.accentBg,
                border: `1px solid ${theme.color.border}`, display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8,
              }}>
                <div style={{ fontSize: 12, color: theme.color.subtle }}>
                  {n} standard{n !== 1 ? "s" : ""} selected — your {pkg.name} includes its standards allowance; standards beyond it{hasCep ? " and Cyber Essentials Plus" : ""} bill on top. Saved changes update the figure.
                </div>
                {billed !== undefined && (
                  <div style={{ fontSize: 18, fontWeight: 800, color: theme.color.accentText, fontVariantNumeric: "tabular-nums" }}>
                    £{(billed / 100).toLocaleString("en-GB", { maximumFractionDigits: 2 })}<span style={{ fontSize: 12, fontWeight: 400, color: theme.color.faint, marginLeft: 4 }}>/month on top</span>
                  </div>
                )}
              </div>
            );
          }
          return (
            <div style={{
              marginTop: 14, padding: "12px 16px", borderRadius: 3,
              background: n > 0 ? theme.color.accentBg : theme.color.panelAlt,
              border: `1px solid ${n > 0 ? theme.color.accent + "66" : theme.color.border}`,
              display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8,
              transition: "background 0.15s, border-color 0.15s",
            }}>
              <div style={{ fontSize: 12, color: theme.color.subtle }}>
                {n === 0
                  ? "Select standards to see your monthly access fee"
                  : nOther === 0
                  ? `${n} standard${n !== 1 ? "s" : ""} selected${ceSuffix}${saqSuffix}`
                  : nOther === 1
                  ? `${n} standard${n !== 1 ? "s" : ""} selected — £249 base${ceSuffix}${saqSuffix}`
                  : nOther === 2
                  ? `${n} standard${n !== 1 ? "s" : ""} selected — £249 + £149${ceSuffix}${saqSuffix}`
                  : `${n} standard${n !== 1 ? "s" : ""} selected — £398 + £${(nOther - 2) * 99} additional${ceSuffix}${saqSuffix}`}
              </div>
              {n > 0 && (
                <div style={{ fontSize: 18, fontWeight: 800, color: theme.color.accentText, fontVariantNumeric: "tabular-nums" }}>
                  £{monthlyFee}<span style={{ fontSize: 12, fontWeight: 400, color: theme.color.faint, marginLeft: 4 }}>/month</span>
                </div>
              )}
            </div>
          );
        })()}

        {saveDropsReference && (
          <div style={{ marginTop: 12, fontSize: 12, color: theme.color.warningText }}>
            Saving with no standard selected also switches off {enrolledReferenceIds.map((id) => REFERENCE_FRAMEWORKS.find((f) => f.id === id)?.name ?? id).join(", ")} — it is free only alongside a standard.
          </div>
        )}
        {!readOnly && <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 12 }}>
          <button onClick={saveFrameworks} disabled={frameworksSaving} style={{ ...btnPrimary, opacity: frameworksSaving ? 0.7 : 1 }}>
            {frameworksSaving ? "Saving…" : "Save selection"}
          </button>
          {frameworksSaved && <span style={{ fontSize: 12, color: theme.color.successText }}>Saved</span>}
          {frameworksError && <span style={{ fontSize: 12, color: theme.color.dangerText }}>{frameworksError}</span>}
        </div>}
      </div>

      {/* ── B. Service Tier ───────────────────────────────────────────── */}
      <div style={{ ...card, marginBottom: 20 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: theme.color.heading, marginBottom: 12 }}>
          Service Tier
        </div>

        <div style={{ fontSize: 20, fontWeight: 700, color: theme.color.accentText, marginBottom: 4 }}>
          {org.serviceTier.name}
        </div>
        <div style={{ fontSize: 12, color: theme.color.subtle, marginBottom: 8 }}>
          {org.serviceTier.description}
        </div>
        <div style={{ fontSize: 13, color: theme.color.text }}>
          £{(org.serviceTier.basePlatformFeePence / 100).toFixed(2)}/month platform fee
        </div>
        <TierPassThroughNote tier={tiers.find((t) => t.id === org.serviceTier.id) ?? org.serviceTier} offer={consultancyOffer} />

        {pkg && (
          <div style={{ marginTop: 14, padding: "10px 14px", background: theme.color.accentBg, border: `1px solid ${theme.color.accent}55`, borderRadius: 3, fontSize: 12, color: theme.color.text, lineHeight: 1.5 }}>
            Your <strong>{pkg.name}</strong> includes this tier at the package rate.
            {pkg.tierFixed
              ? " To change tier, ask your consultant to move you to a bundle or pay-as-you-go."
              : " You can request a higher tier; a lower one would need your consultant to change your package."}
          </div>
        )}

        {/* Enterprise scaling — the two dimensions above the list price. Logins are unlimited. */}
        {usage && (
          <div style={{ marginTop: 14, padding: "10px 14px", background: theme.color.panelAlt, border: `1px solid ${theme.color.border}`, borderRadius: 3, fontSize: 12, color: theme.color.text, lineHeight: 1.6 }}>
            <div style={{ fontSize: 11, color: theme.color.faint, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 4 }}>Scaling</div>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
              <span>{peopleScalingLabel(usage.peopleScalingRule ?? "list", usage.headcount ?? null)}{usage.headcount == null && " (not set — headcount in My Organisation)"}</span>
              <span style={{ fontVariantNumeric: "tabular-nums", color: (usage.peopleScalingPence ?? 0) > 0 ? theme.color.accentText : theme.color.faint }}>
                {(usage.peopleScalingPence ?? 0) > 0 ? `£${((usage.peopleScalingPence ?? 0) / 100).toFixed(2)}/month` : "list price"}
              </span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
              <span>{endpointScalingLabel(usage.endpointCount ?? 0)}</span>
              <span style={{ fontVariantNumeric: "tabular-nums", color: (usage.endpointScalingPence ?? 0) > 0 ? theme.color.accentText : theme.color.faint }}>
                {(usage.endpointScalingPence ?? 0) > 0 ? `£${((usage.endpointScalingPence ?? 0) / 100).toFixed(2)}/month` : "included"}
              </span>
            </div>
            <div style={{ fontSize: 11, color: theme.color.faint, marginTop: 4 }}>
              Endpoints are the devices and workloads the platform discovered through Intune, Defender, AWS, GCP or Sentinel and has seen in the last 90 days — the first {ENDPOINTS_INCLUDED} are included and assets you add by hand are never billed. Logins are unlimited.
              {(usage.headcount ?? 0) >= SCALING_CONTRACT_NOTES.commitmentPeople && ` At ${SCALING_CONTRACT_NOTES.commitmentPeople.toLocaleString("en-GB")}+ people the contract runs ${SCALING_CONTRACT_NOTES.commitmentMonths} months, with ${SCALING_CONTRACT_NOTES.prepayDiscountPercent}% off for annual prepayment.`}
            </div>
            {usage.enterpriseRequired && (
              <div style={{ marginTop: 8, padding: "8px 12px", background: theme.color.warningBg, border: `1px solid ${theme.color.warning}`, borderRadius: 3, color: theme.color.warningText }}>
                {pkg ? `The ${pkg.name}` : "Pay-as-you-go"} is not offered above {PAYG_PEOPLE_MAX} people in scope — your consultant will move you to the Enterprise bundle.
              </div>
            )}
            {usage.dedicatedQuote && (
              <div style={{ marginTop: 8, padding: "8px 12px", background: theme.color.warningBg, border: `1px solid ${theme.color.warning}`, borderRadius: 3, color: theme.color.warningText }}>
                Your platform fee is held at the {gbpMo(SCALING_CEILING_PENCE)} ceiling — above it, Abacas provides a dedicated-instance quote. Contact sales@abacassecurity.com.
              </div>
            )}
          </div>
        )}

        {pendingRequest && (
          <div style={{ marginTop: 14, padding: "10px 14px", background: theme.color.warningBg, border: `1px solid ${theme.color.warning}`, borderRadius: 3, fontSize: 12, color: theme.color.warningText }}>
            Upgrade to <strong>{pendingRequest.requestedTier.name}</strong> is pending consultant approval — requested{" "}
            {new Date(pendingRequest.requestedAt).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
            {!readOnly && (
              <button
                onClick={() => withdrawRequest(pendingRequest.id)} disabled={withdrawing}
                style={{ marginLeft: 10, padding: "2px 10px", fontSize: 11, fontWeight: 600, borderRadius: 3, border: `1px solid ${theme.color.warning}`, background: "transparent", color: theme.color.warningText, cursor: withdrawing ? "not-allowed" : "pointer" }}
              >
                {withdrawing ? "Withdrawing…" : "Withdraw request"}
              </button>
            )}
            {withdrawError && <div style={{ marginTop: 6, color: theme.color.dangerText }}>{withdrawError}</div>}
            {requestError && <div style={{ marginTop: 6, color: theme.color.dangerText }}>{requestError}</div>}
          </div>
        )}

        {otherTiers.length > 0 && (
          <div style={{ marginTop: 16 }}>
            <div style={{ fontSize: 11, color: theme.color.faint, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 10 }}>
              Request a change — requires consultant approval
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {otherTiers.map((t) => {
                const alreadyRequested = requestedTierId === t.id;
                const hasPending = !!pendingRequest;
                return (
                  <div key={t.id} style={{ padding: "10px 14px", background: theme.color.panelAlt, border: `1px solid ${theme.color.border}`, borderRadius: 3 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 600, color: theme.color.heading }}>
                          {t.name}
                          <span style={{ fontWeight: 500, color: theme.color.subtle, marginLeft: 8 }}>£{(t.basePlatformFeePence / 100).toFixed(2)}/month platform fee</span>
                        </div>
                        <div style={{ fontSize: 11, color: theme.color.subtle, marginTop: 2 }}>{t.description}</div>
                      </div>
                      <button
                        onClick={() => requestUpgrade(t.id)}
                        disabled={alreadyRequested || hasPending || requesting}
                        style={{ ...btnPrimary, opacity: alreadyRequested || hasPending ? 0.55 : 1, cursor: alreadyRequested || hasPending ? "default" : "pointer", flexShrink: 0, marginLeft: 12 }}
                      >
                        {alreadyRequested ? "Requested" : t.level > org.serviceTier.level ? "Request upgrade" : "Request change"}
                      </button>
                    </div>
                    <TierPassThroughNote tier={t} offer={consultancyOffer} compact />
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* ── D. AI Check Frequency ─────────────────────────────────────── */}
      {showAi && <div style={{ ...card, marginBottom: 20 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: theme.color.heading, marginBottom: 4 }}>
          AI Check Frequency
        </div>
        <div style={{ fontSize: 12, color: theme.color.subtle, marginBottom: org.isDemo ? 8 : 16 }}>
          How often automated compliance checks run against your controls.
        </div>
        {org.isDemo && (
          <div style={{ fontSize: 12, color: theme.color.muted, marginBottom: 16 }}>
            Automatic checks are switched off for demo organisations. The schedule is shown as set; a check can still be run by hand from Compliance check.
          </div>
        )}

        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8 }}>
          {FREQUENCY_OPTIONS.map((opt) => {
            const active = org.apiFrequency.toLowerCase() === opt.id;
            const locked = frequencySaving || !!org.isDemo;
            return (
              <button
                key={opt.id}
                onClick={() => setFrequency(opt.id)}
                disabled={locked}
                style={{
                  padding: "12px 10px", borderRadius: theme.radius.md, cursor: locked ? "default" : "pointer",
                  textAlign: "center",
                  border: `1px solid ${active ? theme.color.accent : theme.color.border}`,
                  background: active ? theme.color.accentBg : "transparent",
                  opacity: org.isDemo ? (active ? 0.7 : 0.45) : frequencySaving && !active ? 0.6 : 1,
                  transition: "border-color 0.1s, background 0.1s",
                }}
              >
                <div style={{ fontSize: 13, fontWeight: 700, color: active ? theme.color.accentText : theme.color.heading }}>
                  {opt.label}
                </div>
                <div style={{ fontSize: 11, color: theme.color.subtle, marginTop: 3 }}>
                  {opt.sublabel}
                </div>
              </button>
            );
          })}
        </div>
        {frequencySaving && (
          <div style={{ marginTop: 10, fontSize: 12, color: theme.color.faint }}>Saving…</div>
        )}

        {costEstimate && org && !org.isDemo && org.apiFrequency !== "realtime" && (() => {
          const freq = org.apiFrequency;
          const entry = costEstimate.costByFrequency[freq];
          const perCheck = (costEstimate.costPerCheckPence / 100).toFixed(2);
          const annual = entry ? (entry.annualCostPence / 100).toFixed(2) : null;
          const isEstimate = costEstimate.source === "estimate";
          return (
            <div style={{
              marginTop: 14,
              padding: "10px 14px",
              borderRadius: theme.radius.md,
              background: theme.color.accentBg,
              border: `1px solid ${theme.color.border}`,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 12,
            }}>
              <div>
                <span style={{ fontSize: 12, fontWeight: 600, color: theme.color.heading }}>
                  ~£{perCheck} per check
                </span>
                {annual && (
                  <span style={{ fontSize: 12, color: theme.color.subtle, marginLeft: 8 }}>
                    · ~£{annual}/year at current frequency
                  </span>
                )}
              </div>
              <div style={{ fontSize: 11, color: theme.color.faint, textAlign: "right", flexShrink: 0 }}>
                {isEstimate
                  ? `Estimate based on ${costEstimate.frameworkCount} enrolled standard${costEstimate.frameworkCount !== 1 ? "s" : ""}`
                  : `Based on last ${costEstimate.historicalChecksUsed} check${costEstimate.historicalChecksUsed !== 1 ? "s" : ""}`
                }
              </div>
            </div>
          );
        })()}
      </div>}

      {/* ── E. Standards Access Fee ───────────────────────────────────── */}
      {(() => {
        const hasCe  = selectedFrameworks.includes("cyber_essentials");
        const hasCep = selectedFrameworks.includes("cyber_essentials_plus");
        const ceFee  = (hasCe ? 49 : 0) + (hasCep ? 149 : 0);
        const otherFrameworks = selectedFrameworks.filter((id) => !NO_FEE_IDS.has(id) && id !== "cyber_essentials" && id !== "cyber_essentials_plus");
        const nOther = otherFrameworks.length;
        const otherFee = nOther === 0 ? 0 : nOther === 1 ? 249 : 398 + (nOther - 2) * 99;
        const n = selectedFrameworks.filter((id) => !NO_FEE_IDS.has(id)).length;
        const pciSaqCount = selectedFrameworks.includes("pci_dss") ? selectedPciSaqs.length : 0;
        const pciSaqExtra = pciSaqFee(pciSaqCount) / 100;
        const monthlyFee = ceFee + otherFee + pciSaqExtra;
        return (
          <div style={{ ...card, marginBottom: 20 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 4 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: theme.color.heading }}>
                Standards Access Fee
              </div>
              {n > 0 && (
                <div style={{ fontSize: 13, color: theme.color.subtle }}>
                  Your current selection:{" "}
                  <strong style={{ fontSize: 16, color: theme.color.accentText }}>£{monthlyFee}</strong>
                  <span style={{ fontSize: 11, color: theme.color.faint, marginLeft: 4 }}>/month</span>
                </div>
              )}
            </div>
            <div style={{ fontSize: 12, color: theme.color.subtle, marginBottom: 16 }}>
              A monthly fee applies for each compliance standard you enrol in. Reference frameworks (NIST CSF 2.0) are free alongside any standard.
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {/* Cyber Essentials flat-rate rows */}
              {[
                { id: "cyber_essentials",      label: "Cyber Essentials",  price: "£49/mo",  note: "flat monthly rate",  active: hasCe  },
                { id: "cyber_essentials_plus",  label: "Cyber Essentials Plus", price: "£149/mo", note: "flat monthly rate",  active: hasCep },
              ].map(({ id, label, price, note, active }) => (
                <div key={id} style={{
                  display: "flex", alignItems: "center", gap: 12, padding: "10px 14px",
                  background: active ? theme.color.accentBg : theme.color.panelAlt,
                  border: `1px solid ${active ? theme.color.accent : theme.color.border}`,
                  borderRadius: 3,
                  transition: "background 0.15s, border-color 0.15s",
                }}>
                  <span style={{ fontSize: 13, color: active ? theme.color.heading : theme.color.text, flex: 1, fontWeight: active ? 600 : 400 }}>{label}</span>
                  <span style={{ fontSize: 15, fontWeight: 700, color: active ? theme.color.accentText : theme.color.faint, fontVariantNumeric: "tabular-nums" }}>{price}</span>
                  <span style={{ fontSize: 11, color: theme.color.faint, minWidth: 180, textAlign: "right" }}>{note}</span>
                </div>
              ))}
              {/* Tiered rows for all other standards */}
              {(hasCe || hasCep) && nOther > 0 && (
                <div style={{ marginTop: 4, marginBottom: 2, fontSize: 11, color: theme.color.faint, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                  Other standards
                </div>
              )}
              {[
                { label: "1st standard",    price: "£249/mo",  note: "base monthly access fee",        active: nOther >= 1 },
                { label: "2nd standard",    price: "+£149/mo", note: "added to the same month",        active: nOther >= 2 },
                { label: "Each additional", price: "+£99/mo",  note: "per extra framework beyond two", active: nOther >= 3 },
              ].map(({ label, price, note, active }) => (
                <div key={label} style={{
                  display: "flex", alignItems: "center", gap: 12, padding: "10px 14px",
                  background: active ? theme.color.accentBg : theme.color.panelAlt,
                  border: `1px solid ${active ? theme.color.accent : theme.color.border}`,
                  borderRadius: 3,
                  transition: "background 0.15s, border-color 0.15s",
                }}>
                  <span style={{ fontSize: 13, color: active ? theme.color.heading : theme.color.text, flex: 1, fontWeight: active ? 600 : 400 }}>{label}</span>
                  <span style={{ fontSize: 15, fontWeight: 700, color: active ? theme.color.accentText : theme.color.faint, fontVariantNumeric: "tabular-nums" }}>{price}</span>
                  <span style={{ fontSize: 11, color: theme.color.faint, minWidth: 180, textAlign: "right" }}>{note}</span>
                </div>
              ))}
              {REFERENCE_FRAMEWORKS.filter((fw) => enrolledIds.includes(fw.id)).map((fw) => (
                <div key={fw.id} style={{
                  display: "flex", alignItems: "center", gap: 12, padding: "10px 14px",
                  background: theme.color.accentBg, border: `1px solid ${theme.color.accent}`, borderRadius: 3,
                }}>
                  <span style={{ fontSize: 13, color: theme.color.heading, flex: 1, fontWeight: 600 }}>{fw.name}</span>
                  <span style={{ fontSize: 15, fontWeight: 700, color: theme.color.accentText }}>Free</span>
                  <span style={{ fontSize: 11, color: theme.color.faint, minWidth: 180, textAlign: "right" }}>reference framework — free with a standard</span>
                </div>
              ))}
              {selectedFrameworks.includes("pci_dss") && <>
                <div style={{ marginTop: 4, marginBottom: 2, fontSize: 11, color: theme.color.faint, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                  PCI DSS SAQ types
                </div>
                {[
                  { label: "1st SAQ type",        price: "included", note: "covered by your framework fee",        active: pciSaqCount >= 1 },
                  { label: "2nd SAQ type",         price: "+£19/mo",  note: "second SAQ type selected",             active: pciSaqCount >= 2 },
                  { label: "3rd+ SAQ type (each)", price: "+£9/mo",   note: "per additional SAQ type beyond two",   active: pciSaqCount >= 3 },
                ].map(({ label, price, note, active }) => (
                  <div key={label} style={{
                    display: "flex", alignItems: "center", gap: 12, padding: "10px 14px",
                    background: active ? theme.color.accentBg : theme.color.panelAlt,
                    border: `1px solid ${active ? theme.color.accent : theme.color.border}`,
                    borderRadius: 3,
                    transition: "background 0.15s, border-color 0.15s",
                  }}>
                    <span style={{ fontSize: 13, color: active ? theme.color.heading : theme.color.text, flex: 1, fontWeight: active ? 600 : 400 }}>{label}</span>
                    <span style={{ fontSize: 15, fontWeight: 700, color: active ? theme.color.accentText : theme.color.faint, fontVariantNumeric: "tabular-nums" }}>{price}</span>
                    <span style={{ fontSize: 11, color: theme.color.faint, minWidth: 180, textAlign: "right" }}>{note}</span>
                  </div>
                ))}
              </>}
            </div>
            {n === 0 && (
              <div style={{ marginTop: 12, fontSize: 12, color: theme.color.faint }}>
                Select frameworks above to see your monthly access fee.
              </div>
            )}
            {(nOther >= 3 || pciSaqExtra > 0 || (ceFee > 0 && nOther > 0)) && (
              <div style={{ marginTop: 12, fontSize: 12, color: theme.color.subtle }}>
                {nOther >= 3 ? `${nOther} other standards — £398 + £${(nOther - 2) * 99}` : nOther === 2 ? "2 other standards — £398" : nOther === 1 ? "1 other standard — £249" : ""}
                {ceFee > 0 && nOther > 0 ? ` + CE fees £${ceFee}` : ceFee > 0 ? `CE fees £${ceFee}` : ""}
                {pciSaqExtra > 0 ? ` + £${pciSaqExtra} SAQ types` : ""} = <strong style={{ color: theme.color.text }}>£{monthlyFee}/month</strong>
              </div>
            )}
          </div>
        );
      })()}

      {/* ── E1. Licensed copies of the enrolled standards ─────────────── */}
      <StandardLicencesPanel key={enrolledIds.join(",")} />

      {/* ── E. Legal Obligations ─────────────────────────────────────── */}
      {(() => {
        const countries: string[] = (() => { try { return JSON.parse(org.geography ?? "[]"); } catch { return []; } })();
        const hasEuJurisdiction = countries.some((c) => EU_DORA_COUNTRIES.has(c));
        if (!hasEuJurisdiction && !addons?.doraMonitoring) return null;
        return (
      <div style={{ ...card, marginBottom: 20 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: theme.color.heading, marginBottom: 4 }}>
          Legal Obligations
        </div>
        <div style={{ fontSize: 12, color: theme.color.subtle, marginBottom: 16 }}>
          Modules required by regulation for your operating jurisdictions. Contact your consultant to activate.
        </div>

        {/* DORA monitoring */}
        <div style={{
          display: "flex", alignItems: "center", gap: 16,
          padding: "14px 16px", borderRadius: 3,
          border: `1px solid ${addons?.doraMonitoring ? theme.color.accent : theme.color.border}`,
          background: addons?.doraMonitoring ? theme.color.accentBg : "transparent",
          transition: "border-color 0.15s, background 0.15s",
        }}>
          <div style={{
            width: 10, height: 10, borderRadius: "50%", flexShrink: 0,
            background: addons?.doraMonitoring ? theme.color.success : theme.color.border,
            boxShadow: addons?.doraMonitoring ? `0 0 0 3px ${theme.color.success}33` : "none",
          }} />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: theme.color.heading }}>
              DORA Monitoring
            </div>
            <div style={{ fontSize: 12, color: theme.color.subtle, marginTop: 2, lineHeight: 1.4 }}>
              Continuous tracking of ICT incident reporting deadlines (4h/72h/1-month), resilience testing schedule, and ICT third-party provider register — required under Regulation (EU) 2022/2554.
            </div>
          </div>
          <div style={{ textAlign: "right", flexShrink: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: addons?.doraMonitoring ? theme.color.accentText : theme.color.faint, fontVariantNumeric: "tabular-nums" }}>
              {gbp(DORA_MONITORING_ADDON_PENCE)}<span style={{ fontSize: 11, fontWeight: 400, color: theme.color.faint, marginLeft: 2 }}>/month</span>
            </div>
            <div style={{
              marginTop: 4, fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 3, display: "inline-block",
              background: addons?.doraMonitoring ? theme.color.successBg : theme.color.border,
              color: addons?.doraMonitoring ? theme.color.successText : theme.color.muted,
              textTransform: "uppercase", letterSpacing: "0.04em",
            }}>
              {addons?.doraMonitoring ? "Active" : "Inactive"}
            </div>
          </div>
        </div>

        <div style={{ marginTop: 10, fontSize: 12, color: theme.color.faint }}>
          To activate or deactivate DORA Monitoring, ask your consultant to make the change in the consultant portal.
        </div>
      </div>
        );
      })()}

      {/* ── F. Optional Add-ons ──────────────────────────────────────── */}
      <div style={{ ...card, marginBottom: 20 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: theme.color.heading, marginBottom: 4 }}>
          Optional Add-ons
        </div>
        <div style={{ fontSize: 12, color: theme.color.subtle, marginBottom: 16 }}>
          Add-ons extend the platform with specialist modules.
        </div>

        {/* TPRM add-on */}
        {tprmError && <div style={{ fontSize: 12, color: theme.color.dangerText, marginBottom: 8 }}>{tprmError}</div>}
        <div style={{
          display: "flex", alignItems: "center", gap: 16,
          padding: "14px 16px", borderRadius: 3,
          border: `1px solid ${addons?.tprm ? theme.color.accent : theme.color.border}`,
          background: addons?.tprm ? theme.color.accentBg : "transparent",
          transition: "border-color 0.15s, background 0.15s",
        }}>
          {/* Status indicator */}
          <div style={{
            width: 10, height: 10, borderRadius: "50%", flexShrink: 0,
            background: addons?.tprm ? theme.color.success : theme.color.border,
            boxShadow: addons?.tprm ? `0 0 0 3px ${theme.color.success}33` : "none",
          }} />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: theme.color.heading }}>
              Third-Party Risk Management (TPRM)
            </div>
            <div style={{ fontSize: 12, color: theme.color.subtle, marginTop: 2, lineHeight: 1.4 }}>
              Supplier onboarding questionnaires, risk scoring, and ongoing vendor monitoring integrated into your compliance posture.
            </div>
          </div>
          <div style={{ textAlign: "right", flexShrink: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: addons?.tprm ? theme.color.accentText : theme.color.faint, fontVariantNumeric: "tabular-nums" }}>
              {gbp(TPRM_ADDON_PENCE)}<span style={{ fontSize: 11, fontWeight: 400, color: theme.color.faint, marginLeft: 2 }}>/month</span>
            </div>
            <div style={{
              marginTop: 4, fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 3, display: "inline-block",
              background: addons?.tprm ? theme.color.successBg : theme.color.border,
              color: addons?.tprm ? theme.color.successText : theme.color.muted,
              textTransform: "uppercase", letterSpacing: "0.04em",
            }}>
              {addons?.tprm ? "Active" : "Inactive"}
            </div>
            <button
              onClick={toggleTprm}
              disabled={savingTprm || !addons}
              style={{
                display: "block", marginTop: 6,
                background: addons?.tprm ? "none" : theme.color.accent,
                border: addons?.tprm ? `1px solid ${theme.color.border}` : "none",
                borderRadius: 3, padding: "5px 12px", fontSize: 12, cursor: "pointer",
                color: addons?.tprm ? theme.color.subtle : "#fff",
                opacity: (savingTprm || !addons) ? 0.5 : 1, fontWeight: 600,
              }}
            >
              {savingTprm ? "Saving…" : addons?.tprm ? "Disable" : "Enable"}
            </button>
          </div>
        </div>

        {/* Environment & Sustainability Governance add-on */}
        {esgError && <div style={{ fontSize: 12, color: theme.color.dangerText, marginTop: 8 }}>{esgError}</div>}
        <div style={{
          display: "flex", alignItems: "center", gap: 16,
          padding: "14px 16px", borderRadius: 3, marginTop: 8,
          border: `1px solid ${addons?.esg ? theme.color.accent : theme.color.border}`,
          background: addons?.esg ? theme.color.accentBg : "transparent",
          transition: "border-color 0.15s, background 0.15s",
        }}>
          <div style={{
            width: 10, height: 10, borderRadius: "50%", flexShrink: 0,
            background: addons?.esg ? theme.color.success : theme.color.border,
            boxShadow: addons?.esg ? `0 0 0 3px ${theme.color.success}33` : "none",
          }} />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: theme.color.heading }}>
              Environment &amp; Sustainability Governance
            </div>
            <div style={{ fontSize: 12, color: theme.color.subtle, marginTop: 2, lineHeight: 1.4 }}>
              Enrols you in ISO 14001:2015 Environmental Management System and IWA 48:2024 ESG Implementation Principles — track environmental compliance, set targets, and manage your ESG maturity. Both standards are covered by this price; they appear under Environment &amp; Sustainability in the sidebar while it is active.
            </div>
          </div>
          <div style={{ textAlign: "right", flexShrink: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: addons?.esg ? theme.color.accentText : theme.color.faint, fontVariantNumeric: "tabular-nums" }}>
              {gbp(ESG_ADDON_PENCE)}<span style={{ fontSize: 11, fontWeight: 400, color: theme.color.faint, marginLeft: 2 }}>/month</span>
            </div>
            <div style={{
              marginTop: 4, fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 3, display: "inline-block",
              background: addons?.esg ? theme.color.successBg : theme.color.border,
              color: addons?.esg ? theme.color.successText : theme.color.muted,
              textTransform: "uppercase", letterSpacing: "0.04em",
            }}>
              {addons?.esg ? "Active" : "Inactive"}
            </div>
            <button
              onClick={toggleEsg}
              disabled={savingEsg || !addons}
              style={{
                display: "block", marginTop: 6,
                background: addons?.esg ? "none" : theme.color.accent,
                border: addons?.esg ? `1px solid ${theme.color.border}` : "none",
                borderRadius: 3, padding: "5px 12px", fontSize: 12, cursor: "pointer",
                color: addons?.esg ? theme.color.subtle : "#fff",
                opacity: (savingEsg || !addons) ? 0.5 : 1, fontWeight: 600,
              }}
            >
              {savingEsg ? "Saving…" : addons?.esg ? "Disable" : "Enable"}
            </button>
          </div>
        </div>

        {/* Auditor access add-on: what Full Service includes, for L1-L4 (backend lib/auditorAccess.ts) */}
        {org.serviceTier.level < 5 && (
          <>
            {auditorError && <div style={{ fontSize: 12, color: theme.color.dangerText, marginTop: 8 }}>{auditorError}</div>}
            <div style={{
              display: "flex", alignItems: "center", gap: 16,
              padding: "14px 16px", borderRadius: 3, marginTop: 8,
              border: `1px solid ${addons?.auditorAccess ? theme.color.accent : theme.color.border}`,
              background: addons?.auditorAccess ? theme.color.accentBg : "transparent",
              transition: "border-color 0.15s, background 0.15s",
            }}>
              <div style={{
                width: 10, height: 10, borderRadius: "50%", flexShrink: 0,
                background: addons?.auditorAccess ? theme.color.success : theme.color.border,
                boxShadow: addons?.auditorAccess ? `0 0 0 3px ${theme.color.success}33` : "none",
              }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: theme.color.heading }}>Auditor access</div>
                <div style={{ fontSize: 12, color: theme.color.subtle, marginTop: 2, lineHeight: 1.4 }}>
                  Engage a certification body and work its findings and evidence requests on the platform without moving to Full Service. The certification body&apos;s own fees are separate.
                </div>
              </div>
              <div style={{ textAlign: "right", flexShrink: 0 }}>
                <div style={{ fontSize: 15, fontWeight: 700, color: addons?.auditorAccess ? theme.color.accentText : theme.color.faint, fontVariantNumeric: "tabular-nums" }}>
                  {gbp(AUDITOR_ACCESS_ADDON_PENCE)}<span style={{ fontSize: 11, fontWeight: 400, color: theme.color.faint, marginLeft: 2 }}>/month</span>
                </div>
                <div style={{
                  marginTop: 4, fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 3, display: "inline-block",
                  background: addons?.auditorAccess ? theme.color.successBg : theme.color.border,
                  color: addons?.auditorAccess ? theme.color.successText : theme.color.muted,
                  textTransform: "uppercase", letterSpacing: "0.04em",
                }}>
                  {addons?.auditorAccess ? "Active" : "Inactive"}
                </div>
                <button
                  onClick={toggleAuditorAccess}
                  disabled={savingAuditor || !addons}
                  style={{
                    display: "block", marginTop: 6,
                    background: addons?.auditorAccess ? "none" : theme.color.accent,
                    border: addons?.auditorAccess ? `1px solid ${theme.color.border}` : "none",
                    borderRadius: 3, padding: "5px 12px", fontSize: 12, cursor: "pointer",
                    color: addons?.auditorAccess ? theme.color.subtle : "#fff",
                    opacity: (savingAuditor || !addons) ? 0.5 : 1, fontWeight: 600,
                  }}
                >
                  {savingAuditor ? "Saving…" : addons?.auditorAccess ? "Disable" : "Enable"}
                </button>
              </div>
            </div>
          </>
        )}

        {/* Jurisdiction Law & Guidance add-on */}
        {legalError && <div style={{ fontSize: 12, color: theme.color.dangerText, marginTop: 8 }}>{legalError}</div>}
        <div style={{
          display: "flex", alignItems: "center", gap: 16,
          padding: "14px 16px", borderRadius: 3, marginTop: 8,
          border: `1px solid ${addons?.legalObligations ? theme.color.accent : theme.color.border}`,
          background: addons?.legalObligations ? theme.color.accentBg : "transparent",
          transition: "border-color 0.15s, background 0.15s",
        }}>
          <div style={{
            width: 10, height: 10, borderRadius: "50%", flexShrink: 0,
            background: addons?.legalObligations ? theme.color.success : theme.color.border,
            boxShadow: addons?.legalObligations ? `0 0 0 3px ${theme.color.success}33` : "none",
          }} />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: theme.color.heading }}>
              Jurisdiction Law & Guidance
            </div>
            <div style={{ fontSize: 12, color: theme.color.subtle, marginTop: 2, lineHeight: 1.4 }}>
              Access to the applicable laws, regulations, and compliance guidance for your operating jurisdictions.
              {addons?.legalObligations && usage && (usage.jurisdictionCount ?? 0) > 0
                ? ` ${usage.jurisdictionCount} jurisdiction${usage.jurisdictionCount !== 1 ? "s" : ""} active — configure in My Organisation → Operating Locations.`
                : " Set your operating countries in My Organisation to activate."}
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            {addons?.legalObligations && usage && (usage.jurisdictionCount ?? 0) > 0 && (
              <div style={{ textAlign: "right", flexShrink: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: theme.color.accentText, fontVariantNumeric: "tabular-nums" }}>
                  £{((usage.jurisdictionAddonPence ?? 0) / 100).toFixed(0)}
                  <span style={{ fontSize: 11, fontWeight: 400, color: theme.color.faint, marginLeft: 2 }}>/month</span>
                </div>
                <div style={{ fontSize: 11, color: theme.color.subtle }}>{jurisdictionFeeNote(usage)}</div>
              </div>
            )}
            <div style={{
              fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 3,
              background: addons?.legalObligations ? theme.color.successBg : theme.color.border,
              color: addons?.legalObligations ? theme.color.successText : theme.color.muted,
              textTransform: "uppercase", letterSpacing: "0.04em",
            }}>
              {addons?.legalObligations ? "Active" : "Inactive"}
            </div>
            <button
              onClick={toggleLegal}
              disabled={savingLegal || !addons}
              style={{
                background: addons?.legalObligations ? "none" : theme.color.accent,
                border: addons?.legalObligations ? `1px solid ${theme.color.border}` : "none",
                borderRadius: 3, padding: "5px 12px", fontSize: 12, cursor: "pointer",
                color: addons?.legalObligations ? theme.color.subtle : "#fff",
                opacity: (savingLegal || !addons) ? 0.5 : 1, fontWeight: 600,
              }}
            >
              {savingLegal ? "Saving…" : addons?.legalObligations ? "Disable" : "Enable"}
            </button>
          </div>
        </div>

        <div style={{ marginTop: 10, fontSize: 12, color: theme.color.faint }}>
          TPRM, ESG, and Jurisdiction Law &amp; Guidance can be enabled or disabled here. DORA Monitoring is managed by your consultant via the consultant portal.
        </div>
      </div>

      {/* ── G. CIS Hardening Benchmarks ───────────────────────────────── */}
      <div style={{ ...card, marginBottom: 20, padding: 0, overflow: "hidden" }}>
        {/* Header row — always visible */}
        <button
          onClick={() => setHardeningOpen((v) => !v)}
          style={{
            width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between",
            padding: "14px 16px", border: "none", background: "transparent", cursor: "pointer", textAlign: "left",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: theme.color.heading }}>CIS Hardening Benchmarks</div>
            {!hardeningLoading && (() => {
              const activeCount = Object.values(hardeningToggles).filter(Boolean).length;
              return activeCount > 0 ? (
                <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 3, background: theme.color.successBg, color: theme.color.successText }}>
                  {activeCount} active
                </span>
              ) : (
                <span style={{ fontSize: 10, color: theme.color.faint }}>none active · {gbpMo(HARDENING_BENCHMARK_PENCE)} each</span>
              );
            })()}
          </div>
          <span style={{ fontSize: 11, color: theme.color.faint }}>{hardeningOpen ? "▲" : "▼"}</span>
        </button>

        {/* Expandable body */}
        {hardeningOpen && (
          <div style={{ borderTop: `1px solid ${theme.color.border}`, padding: "14px 16px" }}>
            <div style={{ fontSize: 12, color: theme.color.subtle, marginBottom: 14 }}>
              {consultantView
                ? `Subscribe this organisation to CIS Microsoft benchmark modules. Each active benchmark is ${gbpMo(HARDENING_BENCHMARK_PENCE)}.`
                : `Enable CIS Microsoft hardening benchmark modules for your organisation. Each active benchmark is ${gbpMo(HARDENING_BENCHMARK_PENCE)}.`}
            </div>
            {!hardeningAvailable && (
              <div style={{ fontSize: 12, color: theme.color.warningText, marginBottom: 12 }}>
                CIS hardening benchmarks are included from Self-Service + AI (tier 3) upwards. {consultantView ? "Move the client up a tier to subscribe them." : "Request a tier change above to subscribe."}
              </div>
            )}
            {hardeningLoading && <div style={{ fontSize: 12, color: theme.color.faint }}>Loading benchmarks…</div>}
            {!hardeningLoading && hardeningBenchmarks.length === 0 && (
              <div style={{ fontSize: 12, color: theme.color.faint }}>No benchmark modules available.</div>
            )}
            {hardeningError && <div style={{ fontSize: 12, color: theme.color.dangerText, marginBottom: 12 }}>{hardeningError}</div>}
            {HARDENING_CATEGORIES.map((cat) => {
              const benchmarks = hardeningBenchmarks.filter((b) => b.category === cat.id);
              if (benchmarks.length === 0) return null;
              const open = hardeningCatOpen[cat.id] !== false;
              const activeCount = benchmarks.filter((b) => hardeningToggles[b.id]).length;
              return (
                <div key={cat.id} style={{ marginBottom: 8, border: `1px solid ${theme.color.border}`, borderRadius: 3, overflow: "hidden" }}>
                  <button
                    onClick={() => setHardeningCatOpen((prev) => ({ ...prev, [cat.id]: !open }))}
                    style={{
                      width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between",
                      padding: "10px 14px", border: "none", background: "transparent", cursor: "pointer",
                      fontSize: 12.5, fontWeight: 600, color: theme.color.heading, textAlign: "left",
                    }}
                  >
                    <span>{cat.label}</span>
                    <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      {activeCount > 0 && (
                        <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 7px", borderRadius: 3, background: theme.color.successBg, color: theme.color.successText }}>
                          {activeCount} active
                        </span>
                      )}
                      <span style={{ color: theme.color.faint, fontSize: 11 }}>{open ? "▲" : "▼"}</span>
                    </span>
                  </button>
                  {open && (
                    <div style={{ borderTop: `1px solid ${theme.color.border}` }}>
                      {benchmarks.map((b) => {
                        const active = hardeningToggles[b.id] ?? false;
                        return (
                          <div
                            key={b.id}
                            style={{
                              display: "flex", alignItems: "center", gap: 12, padding: "10px 14px",
                              background: active ? theme.color.accentBg : "transparent",
                              borderBottom: `1px solid ${theme.color.border}`,
                              transition: "background 0.15s",
                            }}
                          >
                            <div style={{ flex: 1 }}>
                              <div style={{ fontSize: 12.5, fontWeight: 500, color: theme.color.text }}>{b.name}</div>
                              <div style={{ fontSize: 11, color: theme.color.faint, marginTop: 1 }}>{b.version}</div>
                            </div>
                            <div style={{ display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
                              <span style={{ fontSize: 13, fontWeight: 700, color: active ? theme.color.accentText : theme.color.faint }}>
                                {gbp(HARDENING_BENCHMARK_PENCE)}<span style={{ fontSize: 10, fontWeight: 400, color: theme.color.faint }}>/mo</span>
                              </span>
                              <button
                                onClick={() => toggleHardeningBenchmark(b.id, !active)}
                                disabled={!active && !hardeningAvailable}
                                title={!active && !hardeningAvailable ? "Requires Self-Service + AI or above" : undefined}
                                style={{
                                  fontSize: 11, fontWeight: 700, padding: "4px 10px", borderRadius: 3,
                                  cursor: !active && !hardeningAvailable ? "not-allowed" : "pointer",
                                  opacity: !active && !hardeningAvailable ? 0.45 : 1,
                                  background: active ? "none" : theme.color.accent,
                                  border: active ? `1px solid ${theme.color.border}` : "none",
                                  color: active ? theme.color.subtle : "#fff",
                                }}
                              >
                                {active ? "Remove" : "Add"}
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
            {hardeningBenchmarks.length > 0 && (
              <div style={{ marginTop: 10, fontSize: 11, color: theme.color.faint }}>
                Changes take effect immediately. {consultantView ? "The client will see subscribed benchmarks in their sidebar." : "Subscribed benchmarks appear in your sidebar."}
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── G. Certification Bureaux — one per standard ────────────────── */}
      <div style={{ ...card, marginBottom: 20 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: theme.color.heading, marginBottom: 4 }}>
          Certification Bureaux
        </div>
        <div style={{ fontSize: 12, color: theme.color.subtle, marginBottom: 16 }}>
          Engage a certification or assessment body for each standard you want assessed. You may use different
          bureaux for different standards. Each standard is locked with its bureau for the 3-year certification
          cycle; your consultant can release a lock early.
        </div>
        {bureauError && (
          <div style={{ marginBottom: 12, padding: "8px 12px", background: theme.color.dangerBg, border: `1px solid ${theme.color.danger}`, borderRadius: 3, fontSize: 12, color: theme.color.dangerText }}>
            {bureauError}
          </div>
        )}

        {certifiableFrameworks.length === 0 ? (
          <div style={{ fontSize: 13, color: theme.color.faint }}>
            Enrol in a certifiable standard above to engage a bureau for it.
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {certifiableFrameworks.map((fw) => {
              const engagement = org.engagements.find((e) => e.frameworkId === fw) ?? null;
              const eligible   = bureauList.filter((b) => b.frameworks.includes(fw));
              const isPicking  = engagingFrameworkId === fw;
              const priceFor   = (b: BureauListItem) => b.frameworkPrices.find((p) => p.frameworkId === fw) ?? null;
              const monthlyFor = (p: FrameworkPrice | null) => (p ? Math.ceil(p.certFeePence / 36) + Math.ceil(p.annualFeePence / 12) : null);
              const currentBureau = engagement ? (bureauList.find((b) => b.id === engagement.bureau.id) ?? null) : null;
              const currentPrice = currentBureau ? monthlyFor(priceFor(currentBureau)) : null;
              return (
                <div
                  key={fw}
                  style={{
                    borderRadius: 3, overflow: "hidden",
                    border: `1px solid ${engagement ? theme.color.accent : theme.color.border}`,
                    background: engagement ? theme.color.accentBg + "44" : "transparent",
                  }}
                >
                  {/* Row: standard, its bureau, lock state */}
                  <div style={{ padding: "12px 16px", display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
                    <div style={{ minWidth: 150 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: theme.color.heading }}>{frameworkLabel(fw)}</div>
                      {engagement ? (
                        <div style={{ fontSize: 11, color: theme.color.subtle, marginTop: 2 }}>
                          {engagement.bureau.name}
                          {[engagement.bureau.accreditation, engagement.bureau.country].filter(Boolean).length > 0 && (
                            <> · {[engagement.bureau.accreditation, engagement.bureau.country].filter(Boolean).join(" · ")}</>
                          )}
                        </div>
                      ) : (
                        <div style={{ fontSize: 11, color: theme.color.faint, marginTop: 2, fontStyle: "italic" }}>Not yet put forward for certification</div>
                      )}
                    </div>
                    <div style={{ flex: 1 }} />
                    {engagement && currentPrice !== null && (
                      <div style={{ textAlign: "right" }}>
                        <div style={{ fontSize: 14, fontWeight: 700, color: theme.color.accentText }}>£{(currentPrice / 100).toFixed(2)}</div>
                        <div style={{ fontSize: 10, color: theme.color.subtle }}>/month</div>
                      </div>
                    )}
                    {engagement?.locked ? (
                      <span style={{ fontSize: 11, fontWeight: 600, padding: "3px 9px", borderRadius: 3, background: theme.color.warningBg, color: theme.color.warningText, border: `1px solid ${theme.color.warning}` }}>
                        Locked until {new Date(engagement.lockedUntil!).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
                      </span>
                    ) : eligible.length === 0 ? (
                      <span style={{ fontSize: 11, color: theme.color.faint }}>No listed bureau is accredited for this standard yet</span>
                    ) : (
                      <button
                        onClick={() => {
                          setBureauError("");
                          if (isPicking) { setEngagingFrameworkId(null); setPendingBureauId(null); }
                          else { setEngagingFrameworkId(fw); setPendingBureauId(engagement?.bureau.id ?? null); }
                        }}
                        disabled={bureauSaving}
                        style={isPicking ? secondaryBtn : { ...btnPrimary, fontSize: 12, padding: "6px 14px" }}
                      >
                        {isPicking ? "Cancel" : engagement ? "Change bureau" : "Choose bureau"}
                      </button>
                    )}
                  </div>

                  {/* Picker: bureaux accredited for this standard, with their price for it */}
                  {isPicking && (
                    <div style={{ borderTop: `1px solid ${theme.color.border}`, padding: "12px 16px", background: theme.color.panelAlt, display: "flex", flexDirection: "column", gap: 8 }}>
                      {eligible.map((b) => {
                        const price = priceFor(b);
                        const monthly = monthlyFor(price);
                        const active = pendingBureauId === b.id;
                        return (
                          <label
                            key={b.id}
                            style={{
                              display: "flex", alignItems: "center", gap: 12, cursor: "pointer", padding: "10px 12px", borderRadius: 3,
                              background: active ? theme.color.accentBg : theme.color.panel,
                              border: `1px solid ${active ? theme.color.accent : theme.color.border}`,
                            }}
                          >
                            <input type="radio" name={`bureau-${fw}`} checked={active} onChange={() => setPendingBureauId(b.id)} style={{ cursor: "pointer", flexShrink: 0 }} />
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                                <span style={{ fontSize: 13, fontWeight: 600, color: theme.color.heading }}>{b.name}</span>
                                {b.engagedFrameworkIds.length > 0 && (
                                  <span style={{ fontSize: 10, fontWeight: 700, padding: "1px 7px", borderRadius: 3, background: theme.color.accentBg, color: theme.color.accentText, border: `1px solid ${theme.color.accent}` }}>
                                    Already your bureau for {b.engagedFrameworkIds.map((id) => frameworkLabel(id)).join(", ")}
                                  </span>
                                )}
                              </div>
                              <div style={{ display: "flex", gap: 14, marginTop: 3, flexWrap: "wrap" }}>
                                {b.accreditation && <span style={{ fontSize: 11, color: theme.color.subtle }}>{b.accreditation}</span>}
                                {b.country && <span style={{ fontSize: 11, color: theme.color.subtle }}>{b.country}</span>}
                                {b.contactEmail && (
                                  <a href={`mailto:${b.contactEmail}`} onClick={(e) => e.stopPropagation()} style={{ fontSize: 11, color: theme.color.accentText }}>{b.contactEmail}</a>
                                )}
                                {b.website && (
                                  <a href={safeHref(b.website)} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} style={{ fontSize: 11, color: theme.color.accentText }}>Website</a>
                                )}
                              </div>
                              {b.description && (
                                <div style={{ fontSize: 12, color: theme.color.text, marginTop: 6, lineHeight: 1.5 }}>{b.description}</div>
                              )}
                            </div>
                            <div style={{ textAlign: "right", flexShrink: 0 }}>
                              {monthly !== null && price ? (
                                <>
                                  <div style={{ fontSize: 14, fontWeight: 700, color: theme.color.accentText }}>£{(monthly / 100).toFixed(2)}<span style={{ fontSize: 10, fontWeight: 400, color: theme.color.subtle }}>/mo</span></div>
                                  <div style={{ fontSize: 10, color: theme.color.subtle }}>Cert £{(price.certFeePence / 100).toFixed(0)} · Annual £{(price.annualFeePence / 100).toFixed(0)}</div>
                                </>
                              ) : (
                                <div style={{ fontSize: 10, color: theme.color.faint }}>Price on request</div>
                              )}
                            </div>
                          </label>
                        );
                      })}
                      <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 4, flexWrap: "wrap" }}>
                        <button
                          onClick={() => pendingBureauId && engageBureau(fw, pendingBureauId)}
                          disabled={bureauSaving || !pendingBureauId || pendingBureauId === engagement?.bureau.id}
                          style={{ ...btnPrimary, opacity: bureauSaving || !pendingBureauId || pendingBureauId === engagement?.bureau.id ? 0.6 : 1 }}
                        >
                          {bureauSaving ? "Saving…" : "Engage for this standard"}
                        </button>
                        <span style={{ fontSize: 12, color: theme.color.warningText }}>
                          Locks {frameworkLabel(fw)} with the chosen bureau for 3 years.
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── E. Consultancy Balance (conditional) ──────────────────────── */}
      {showConsultancy && (
        <div style={{ ...card, marginBottom: 20 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: theme.color.heading, marginBottom: 12 }}>
            Consultancy Balance
          </div>
          <div style={{ display: "flex", gap: 28, flexWrap: "wrap" }}>
            <div>
              <div style={{ fontSize: 26, fontWeight: 700, color: theme.color.accentText }}>
                {org.consultancyDaysPerMonth}
              </div>
              <div style={{ fontSize: 11, color: theme.color.subtle, marginTop: 2 }}>full days per month</div>
            </div>
            <div>
              <div style={{ fontSize: 26, fontWeight: 700, color: theme.color.accentText }}>
                {org.consultancyHoursRemaining}
              </div>
              <div style={{ fontSize: 11, color: theme.color.subtle, marginTop: 2 }}>ad-hoc hours remaining</div>
            </div>
          </div>
        </div>
      )}

      {/* ── E2. Billing Arrangement (managed service opt-in) ─────────── */}
      {showConsultancy && org.managedServiceRatePence == null && (
        <div style={{ ...card, marginBottom: 20 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: theme.color.heading, marginBottom: 4 }}>Billing Arrangement</div>
          <div style={{ fontSize: 12, color: theme.color.subtle }}>
            You are billed time &amp; materials for consultant time, at your consultancy's day/hour rates, on top of the platform fee.
            {(() => {
              const rc = consultancyOffer?.rateCard;
              const offers = rc ? RATE_GRADE_ORDER.map((g) => rc[g]).filter((e) => e && e.monthPence > 0) : [];
              return offers.length > 0 ? (
                <> {consultancyOffer?.consultancyName ?? "Your consultancy"} also offers a flat-fee managed service — {offers.map((e, i) => (
                  <span key={e.label}>{i > 0 ? "; " : ""}{e.label} {gbp0(e.monthPence)}/month including {allotment(e.managedDays, e.managedHours)}</span>
                ))}. Ask your consultant to apply it to your account and you can switch here.</>
              ) : (
                <> Your consultant has not yet offered a managed service contract. Contact them if you'd like to discuss a flat-fee arrangement.</>
              );
            })()}
          </div>
        </div>
      )}
      {org.managedServiceRatePence != null && (
        <div style={{ ...card, marginBottom: 20 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: theme.color.heading, marginBottom: 4 }}>
            Billing Arrangement
          </div>
          <div style={{ fontSize: 12, color: theme.color.subtle, marginBottom: 14 }}>
            Your consultant has offered a managed service contract. Switch below to move to a flat monthly fee — your consultant's hourly and daily rates will be hidden, and you can book time within your included allotment.
          </div>

          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 12 }}>
            {/* Managed Service option */}
            <button
              onClick={() => org.billingMode !== "managed_service" && switchBillingMode("managed_service")}
              disabled={switchingBillingMode || org.billingMode === "managed_service"}
              style={{
                flex: 1, minWidth: 200, textAlign: "left", padding: "12px 16px", borderRadius: 3,
                cursor: org.billingMode === "managed_service" ? "default" : "pointer",
                border: `2px solid ${org.billingMode === "managed_service" ? theme.color.accent : theme.color.border}`,
                background: org.billingMode === "managed_service" ? theme.color.accentBg : theme.color.bg,
                opacity: switchingBillingMode && org.billingMode !== "managed_service" ? 0.5 : 1,
              }}
            >
              <div style={{ fontSize: 13, fontWeight: 700, color: org.billingMode === "managed_service" ? theme.color.accentText : theme.color.heading }}>
                Managed Service
              </div>
              <div style={{ fontSize: 12, color: org.billingMode === "managed_service" ? theme.color.accentText : theme.color.muted, marginTop: 2 }}>
                £{((org.managedServiceRatePence ?? 0) / 100).toLocaleString("en-GB")}/month flat fee
              </div>
              {(org.managedServiceDaysPerMonth > 0 || org.managedServiceHoursPerMonth > 0) && (
                <div style={{ fontSize: 11, color: theme.color.subtle, marginTop: 4 }}>
                  Includes{org.managedServiceDaysPerMonth > 0 ? ` ${org.managedServiceDaysPerMonth} day${org.managedServiceDaysPerMonth !== 1 ? "s" : ""}` : ""}{org.managedServiceDaysPerMonth > 0 && org.managedServiceHoursPerMonth > 0 ? " +" : ""}{org.managedServiceHoursPerMonth > 0 ? ` ${org.managedServiceHoursPerMonth} hr${org.managedServiceHoursPerMonth !== 1 ? "s" : ""}` : ""}/month
                </div>
              )}
            </button>

            {/* Time & Materials option */}
            <button
              onClick={() => org.billingMode === "managed_service" && switchBillingMode("time_and_materials")}
              disabled={switchingBillingMode || org.billingMode !== "managed_service"}
              style={{
                flex: 1, minWidth: 200, textAlign: "left", padding: "12px 16px", borderRadius: 3,
                cursor: org.billingMode !== "managed_service" ? "default" : "pointer",
                border: `2px solid ${org.billingMode !== "managed_service" ? theme.color.accent : theme.color.border}`,
                background: org.billingMode !== "managed_service" ? theme.color.accentBg : theme.color.bg,
                opacity: switchingBillingMode && org.billingMode === "managed_service" ? 0.5 : 1,
              }}
            >
              <div style={{ fontSize: 13, fontWeight: 700, color: org.billingMode !== "managed_service" ? theme.color.accentText : theme.color.heading }}>
                Time &amp; Materials
              </div>
              <div style={{ fontSize: 12, color: org.billingMode !== "managed_service" ? theme.color.accentText : theme.color.muted, marginTop: 2 }}>
                Billed per day or hour booked
              </div>
            </button>
          </div>

          {billingModeError && <div style={{ fontSize: 12, color: theme.color.dangerText }}>{billingModeError}</div>}
        </div>
      )}

      {/* ── F. AI Token Budget ────────────────────────────────────────── */}
      {showAi && <div style={{ ...card, marginBottom: 20 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: theme.color.heading, marginBottom: 12 }}>
          AI Token Budget
        </div>

        {usage && usage.tokenBudget > 0 ? (() => {
          const pct = Math.min((usage.tokensUsedThisPeriod / usage.tokenBudget) * 100, 100);
          const barColor = pct >= 100 ? theme.color.danger : pct > 80 ? theme.color.warning : theme.color.success;
          const exhausted = usage.tokensUsedThisPeriod >= usage.tokenBudget;
          return (
            <div style={{ marginBottom: 18 }}>
              {exhausted && (
                <div style={{ marginBottom: 12, padding: "10px 14px", background: cbSafe("#450a0a44"), border: `1px solid ${theme.color.danger}`, borderRadius: 3, fontSize: 12, color: theme.color.dangerText, fontWeight: 600 }}>
                  Token budget exhausted — AI services are suspended. Top up below or wait for the period to reset.
                </div>
              )}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 6 }}>
                <div>
                  <span style={{ fontSize: 26, fontWeight: 800, color: exhausted ? theme.color.danger : theme.color.accentText, fontVariantNumeric: "tabular-nums" }}>
                    {usage.tokensUsedThisPeriod.toLocaleString()}
                  </span>
                  <span style={{ fontSize: 13, color: theme.color.faint, marginLeft: 4 }}>
                    / {usage.tokenBudget.toLocaleString()} tokens
                  </span>
                </div>
                <span style={{ fontSize: 13, fontWeight: 700, color: barColor }}>{pct.toFixed(1)}%</span>
              </div>
              <RulerProgress value={pct} fill={barColor} label="AI tokens used this period" />
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: theme.color.faint, marginTop: 5 }}>
                <span>
                  {usage.tokenRefreshPeriod.charAt(0).toUpperCase() + usage.tokenRefreshPeriod.slice(1)} refresh
                  {usage.tokenPeriodResetsAt ? ` — resets ${new Date(usage.tokenPeriodResetsAt).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}` : ""}
                </span>
                <span>{(usage.tokenBudget - usage.tokensUsedThisPeriod).toLocaleString()} remaining</span>
              </div>
            </div>
          );
        })() : (
          <div style={{ fontSize: 14, fontWeight: 600, color: theme.color.faint, marginBottom: 16 }}>
            No token budget set — AI services run without a limit.
          </div>
        )}

        <div style={{ fontSize: 11, fontWeight: 600, color: theme.color.faint, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 10 }}>
          {usage?.tokenBudget ? "Top up or change budget" : "Set a token budget"}
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <input
            type="number"
            min={0}
            step={1000}
            placeholder="Tokens (e.g. 500000)"
            value={tokenBudgetInput}
            onChange={(e) => setTokenBudgetInput(e.target.value)}
            style={{ ...inputStyle, width: 180 }}
          />
          <select
            value={tokenPeriodInput}
            onChange={(e) => setTokenPeriodInput(e.target.value)}
            style={{ ...inputStyle, width: 120 }}
          >
            <option value="daily">Daily</option>
            <option value="weekly">Weekly</option>
            <option value="monthly">Monthly</option>
            <option value="annual">Annual</option>
          </select>
          <button
            onClick={() => saveTokenBudget(/^\d{1,15}$/.test(tokenBudgetInput.trim().replace(/,/g, "")) ? Number(tokenBudgetInput.trim().replace(/,/g, "")) : NaN)}
            disabled={tokenSaving || !tokenBudgetInput || parseInt(tokenBudgetInput, 10) < 0}
            style={{ ...btnPrimary, opacity: !tokenBudgetInput || tokenSaving ? 0.55 : 1, cursor: !tokenBudgetInput || tokenSaving ? "default" : "pointer" }}
          >
            {tokenSaving ? "Saving…" : "Set budget"}
          </button>
          {usage?.tokenBudget ? (
            <button onClick={() => saveTokenBudget(0)} disabled={tokenSaving} style={secondaryBtn}>
              Remove limit
            </button>
          ) : null}
          {tokenSaved && <span style={{ fontSize: 12, color: theme.color.successText }}>Saved</span>}
          {tokenError && <span style={{ fontSize: 12, color: theme.color.dangerText }}>{tokenError}</span>}
        </div>

        <div style={{ marginTop: 14, padding: "10px 12px", background: theme.color.panelAlt, borderRadius: 3, fontSize: 12, color: theme.color.subtle }}>
          <strong style={{ color: theme.color.muted }}>Indicative pricing:</strong>
          {" "}Input tokens £0.006/1K · Output tokens £0.032/1K.
          A typical compliance check uses approximately 8,000–15,000 tokens.
        </div>

      </div>}

      {/* ── G. Monthly AI Spend Cap ───────────────────────────────────── */}
      {showAi && <div style={{ ...card, marginBottom: 20 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: theme.color.heading, marginBottom: 4 }}>
          Monthly AI Spend Cap
        </div>
        <div style={{ fontSize: 12, color: theme.color.subtle, marginBottom: 16 }}>
          Set a monthly £ limit on AI usage. Ad-hoc checks and template generation stop when the cap is reached; scheduled checks continue.
        </div>

        {org.spendCapPence > 0 ? (() => {
          const spent    = org.currentMonthSpendPence ?? 0;
          const cap      = org.spendCapPence;
          const pct      = Math.min(100, Math.round((spent / cap) * 100));
          const capColor = spent >= cap ? theme.color.danger : pct >= 80 ? theme.color.warning : theme.color.success;
          return (
            <div style={{ marginBottom: 18 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 6 }}>
                <div>
                  <span style={{ fontSize: 26, fontWeight: 800, color: spent >= cap ? theme.color.danger : theme.color.accentText, fontVariantNumeric: "tabular-nums" }}>
                    £{(spent / 100).toFixed(2)}
                  </span>
                  <span style={{ fontSize: 13, color: theme.color.faint, marginLeft: 4 }}>
                    / £{(cap / 100).toFixed(2)} this month
                  </span>
                </div>
                <span style={{ fontSize: 13, fontWeight: 700, color: capColor }}>{pct}%</span>
              </div>
              <RulerProgress value={pct} fill={capColor} label="AI spend this month" />
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: theme.color.faint, marginTop: 5 }}>
                <span>Resets monthly</span>
                <span>£{Math.max(0, (cap - spent) / 100).toFixed(2)} remaining</span>
              </div>
            </div>
          );
        })() : (
          <div style={{ fontSize: 14, fontWeight: 600, color: theme.color.faint, marginBottom: 16 }}>
            No spend cap set — AI usage is uncapped this month.
          </div>
        )}

        <div style={{ fontSize: 11, fontWeight: 600, color: theme.color.faint, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 10 }}>
          {org.spendCapPence > 0 ? "Change cap" : "Set a cap"}
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
            <span style={{ position: "absolute", left: 10, fontSize: 13, color: theme.color.muted, pointerEvents: "none" }}>£</span>
            <input
              type="number"
              min={0}
              step={1}
              placeholder="e.g. 50"
              value={capInput}
              onChange={(e) => setCapInput(e.target.value)}
              style={{ ...inputStyle, width: 140, paddingLeft: 22 }}
            />
          </div>
          <button
            onClick={saveCap}
            disabled={capSaving || !capInput}
            style={{ ...btnPrimary, opacity: !capInput || capSaving ? 0.55 : 1, cursor: !capInput || capSaving ? "default" : "pointer" }}
          >
            {capSaving ? "Saving…" : "Set cap"}
          </button>
          {org.spendCapPence > 0 && (
            <button onClick={removeCap} disabled={capSaving} style={secondaryBtn}>Remove cap</button>
          )}
          {capSaved && <span style={{ fontSize: 12, color: theme.color.successText }}>Saved</span>}
          {capError && <span style={{ fontSize: 12, color: theme.color.dangerText }}>{capError}</span>}
        </div>
      </div>}

      {/* ── G. AI Check Run Allowance (consultancy AI only) ──────────── */}
      {showAi && usage?.hasConsultancyAi && (
        <div style={{ ...card, marginBottom: 20 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: theme.color.heading, marginBottom: 2 }}>
            Monthly Compliance Checks
          </div>
          <div style={{ fontSize: 12, color: theme.color.subtle, marginBottom: 14, lineHeight: 1.5 }}>
            Your consultancy uses a custom AI provider.
            {usage.consultancyAiCheckCap !== null
              ? ` Your monthly allowance is ${usage.consultancyAiCheckCap} compliance check${usage.consultancyAiCheckCap === 1 ? "" : "s"}.`
              : " No monthly limit is currently set."}
            {usage.aiCheckPeriodResetsAt && (
              <> Resets {new Date(usage.aiCheckPeriodResetsAt).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}.</>
            )}
          </div>

          {usage.consultancyAiCheckCap !== null ? (() => {
            const pct = Math.min((usage.aiChecksThisPeriod / usage.consultancyAiCheckCap) * 100, 100);
            const exhausted = usage.aiChecksThisPeriod >= usage.consultancyAiCheckCap;
            return (
              <div>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: theme.color.subtle, marginBottom: 6 }}>
                  <span>{usage.aiChecksThisPeriod} used</span>
                  <span>{usage.consultancyAiCheckCap - usage.aiChecksThisPeriod} remaining</span>
                </div>
                <RulerProgress value={pct} fill={exhausted ? theme.color.danger : pct >= 80 ? theme.color.warning : theme.color.success} label="Compliance checks used this period" />
                {exhausted && (
                  <div style={{ marginTop: 8, fontSize: 12, color: theme.color.dangerText, fontWeight: 600 }}>
                    Monthly limit reached — compliance checks are paused until the next period.
                  </div>
                )}
              </div>
            );
          })() : (
            <div style={{ fontSize: 13, color: theme.color.muted }}>
              {usage.aiChecksThisPeriod} check{usage.aiChecksThisPeriod === 1 ? "" : "s"} run this month
            </div>
          )}
        </div>
      )}

      {/* ── H. AI Usage History ───────────────────────────────────────── */}
      {showAi && usage && (
        <div style={{ ...card, marginBottom: 20 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: theme.color.heading, marginBottom: 14 }}>
            AI Usage History
          </div>

          <div style={{ display: "flex", gap: 32, flexWrap: "wrap", marginBottom: 16 }}>
            <div>
              <div style={{ fontSize: 22, fontWeight: 800, color: theme.color.accentText, fontVariantNumeric: "tabular-nums" }}>
                {usage.totalTokensUsed.toLocaleString()}
              </div>
              <div style={{ fontSize: 11, color: theme.color.subtle, marginTop: 2 }}>total tokens used (all time)</div>
            </div>
            <div>
              <div style={{ fontSize: 22, fontWeight: 800, color: theme.color.accentText }}>
                £{(usage.currentMonthSpendPence / 100).toFixed(2)}
              </div>
              <div style={{ fontSize: 11, color: theme.color.subtle, marginTop: 2 }}>AI spend this month</div>
            </div>
            {(usage.auditorAmountPence ?? 0) > 0 && (
              <div>
                <div style={{ fontSize: 22, fontWeight: 800, color: theme.color.accentText }}>
                  £{((usage.auditorAmountPence ?? 0) / 100).toFixed(2)}
                </div>
                <div style={{ fontSize: 11, color: theme.color.subtle, marginTop: 2 }}>certification bureau fee/month</div>
              </div>
            )}
            {(usage.platformFeePence ?? 0) > 0 && (
              <div>
                <div style={{ fontSize: 22, fontWeight: 800, color: theme.color.heading }}>
                  £{((usage.platformFeePence ?? 0) / 100).toFixed(2)}
                </div>
                <div style={{ fontSize: 11, color: theme.color.subtle, marginTop: 2 }}>estimated monthly bill</div>
              </div>
            )}
          </div>

          {usage.events.length === 0 ? (
            <div style={{ fontSize: 13, color: theme.color.faint }}>No AI usage events yet.</div>
          ) : (
            <>
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                  <thead>
                    <tr>
                      {["Date", "Type", "Tokens in", "Tokens out", "Total tokens", "Cost"].map((h) => (
                        <th key={h} style={{ textAlign: "left", padding: "4px 8px", color: theme.color.faint, fontWeight: 600, borderBottom: `1px solid ${theme.color.line}`, whiteSpace: "nowrap" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {(showAllEvents ? usage.events : usage.events.slice(0, 5)).map((ev) => (
                      <tr key={ev.id}>
                        <td style={{ padding: "6px 8px", color: theme.color.subtle, whiteSpace: "nowrap" }}>
                          {new Date(ev.createdAt).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
                        </td>
                        <td style={{ padding: "6px 8px", color: theme.color.text }}>{formatEventType(ev.eventType)}</td>
                        <td style={{ padding: "6px 8px", color: theme.color.muted, fontVariantNumeric: "tabular-nums" }}>{ev.tokensIn.toLocaleString()}</td>
                        <td style={{ padding: "6px 8px", color: theme.color.muted, fontVariantNumeric: "tabular-nums" }}>{ev.tokensOut.toLocaleString()}</td>
                        <td style={{ padding: "6px 8px", color: theme.color.text, fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>{(ev.tokensIn + ev.tokensOut).toLocaleString()}</td>
                        <td style={{ padding: "6px 8px", color: theme.color.accentText, fontVariantNumeric: "tabular-nums" }}>£{(ev.costPence / 100).toFixed(4)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {usage.events.length > 5 && (
                <button onClick={() => setShowAllEvents((v) => !v)} style={{ ...secondaryBtn, marginTop: 10, fontSize: 12 }}>
                  {showAllEvents ? "Show fewer" : `View all ${usage.events.length} events`}
                </button>
              )}
            </>
          )}
        </div>
      )}

      {/* ── H. Language preference ────────────────────────────────────── */}
      <LanguagePreference />

      {/* ── I. Invoices ───────────────────────────────────────────────── */}
      <div style={card}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: theme.color.heading }}>Invoices</div>
          {consultantView && (
            <button
              onClick={generateInvoice}
              disabled={generatingInvoice}
              style={{ ...btnPrimary, opacity: generatingInvoice ? 0.55 : 1, cursor: generatingInvoice ? "default" : "pointer", fontSize: 12 }}
            >
              {generatingInvoice ? "Generating…" : "Generate last month's invoice"}
            </button>
          )}
        </div>
        {invoiceError && (
          <div style={{ marginBottom: 12, padding: "8px 12px", background: cbSafe("#450a0a"), border: `1px solid ${theme.color.danger}`, borderRadius: 3, fontSize: 12, color: theme.color.dangerText }}>
            {invoiceError}
          </div>
        )}
        {invoices.length === 0 ? (
          <div style={{ fontSize: 13, color: theme.color.faint }}>No invoices yet.</div>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <thead>
              <tr>
                {["Period", "Tier fee", "Usage fee", "Consultancy", "Bureau fee", "Total", "Status"].map((h) => (
                  <th key={h} style={{ textAlign: "left", padding: "4px 8px", color: theme.color.faint, fontWeight: 600, borderBottom: `1px solid ${theme.color.line}` }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {invoices.map((inv) => (
                <tr key={inv.id}>
                  <td style={{ padding: "6px 8px", color: theme.color.text }}>{formatMonthPeriod(inv.periodStart)}</td>
                  <td style={{ padding: "6px 8px", color: theme.color.subtle }}>£{(inv.tierAmountPence / 100).toFixed(2)}</td>
                  <td style={{ padding: "6px 8px", color: theme.color.subtle }}>£{(inv.usageAmountPence / 100).toFixed(2)}</td>
                  <td style={{ padding: "6px 8px", color: theme.color.subtle }}>
                    {(inv.consultancyAmountPence ?? 0) > 0 ? `£${((inv.consultancyAmountPence ?? 0) / 100).toFixed(2)}` : "—"}
                  </td>
                  <td style={{ padding: "6px 8px", color: theme.color.subtle }}>
                    {(inv.auditorAmountPence ?? 0) > 0 ? `£${((inv.auditorAmountPence ?? 0) / 100).toFixed(2)}` : "—"}
                  </td>
                  <td style={{ padding: "6px 8px", color: theme.color.heading, fontWeight: 700 }}>£{(inv.totalAmountPence / 100).toFixed(2)}</td>
                  <td style={{ padding: "6px 8px" }}>
                    <span style={statusBadgeStyle(inv.status)}>{inv.status}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
